/**
 * Wall 3D generation, Phase 2: flattened centerline → Clipper2 inflate (half assembly
 * thickness, miter joins, butt ends) → plan outline MINUS full-height slot rects at each
 * hosted opening (keeps the Phase-1 miter/join model intact) → per-element prism spec,
 * plus header/sill PIECES as elevation-profile extrusions across the thickness
 * (openings.ts — arched heads live in those profiles).
 */
import {
  openingFrameFaces, resolveFrameMount, resolveOpeningFrame,
  centerlineOffsetFt, isConcreteCoreWall, isWoodCoreFloor, isWoodCoreWall,
  offsetPolyline, polylinePointAt, projectToPolyline,
  PLATE_THICKNESS_IN, wallEndArcZFt, wallEndArcRiseFt,
  terrainFitOf, terrainFitLane, faceBands, faceHasBands, isGateOp,
  type Path2D, type PhaseId, type Pt2, type ResolvedElement,
} from '@aj/bim-kernel'
import { sweepProfileAlong } from '../../extrudeSweep'
import { recessBySide, finishFaceOf } from './bandSteps'
import earcut from 'earcut'
import { flattenPath } from '../../flatten'
import { clipperReady, differenceClosed, inflateClosedRing, inflateOpen, intersectClosed, signedArea, unionClosed } from '../../clipper'
import { extrudeOutlines } from '../../extrude'
import { solveRoofForElement } from '../roof/solve'
import { roofPlateFaces, roofUndersideSurfaces } from '../roof/underside'
import { assemblyZonesFt, framingMaterialId } from '../roof/framing'
import { floorPlatformBand, type FloorPlatformBand } from '../floor/framing'
import { slabSolid, slopedCeilingZ } from '../slab/generate3d'
import { foundationGradeAt, foundationLayoutFor, foundationRunPts } from './footing'
import {
  placeOpening, headerProfile, sillProfile, wallFrame, openingProfile,
  isCornerSliderUnit, cornerSliderLegFit, cornerVariantOf, inwardDirAtEnd,
  type OpeningPlacement, type WallFrame,
} from '../../openings'
import { extrudeProfileAcross, type ProfileFrame } from '../../extrudeProfile'
import { wallFaceCircles } from './generate2d'
import { buildWallFraming, type PlateSurf } from './framing'
import { pocketLayout, pocketRoughOpening } from '../opening/pocket'
import { stampOpeningForHost } from '../opening/hostStamp'

/**
 * TRIM round §8.12, DIRECTION FIX (2026-08-20 — Matt's screenshot: "the arch is
 * supposed to face perpendicular to the wall for its length"): an arced wall
 * termination is a varying-height BAND over the wall's own plan footprint whose z
 * follows a circular arc segment ALONG the centerline — the arch reads in the wall
 * FACE, spanning the run (the eyebrow/arched-parapet/scallop read), identical
 * across the thickness. (The first build swept a bullnose section ACROSS the
 * thickness — wrong direction, replaced by this.)
 *
 * WARP FIX (same day — Matt's screenshots): the first band build earcut the
 * densified footprint ring per varying-z prism, and earcut's FAN triangulation
 * (correct for the roof prism's planar surfaces) connected far-apart stations of
 * the NON-planar arc surface into visibly twisted sheets. Triangulation choice
 * matters once z varies along the run — the band is now a RULED LADDER: the sweep
 * primitive with per-station z offsets (extrudeSweep's zOfS/zWeights), pairing
 * matching stations by construction. The rest profile is the band's FULL mid-span
 * rectangle across the thickness; the arc REDUCES the weighted side toward the
 * ends (offset ≤ 0 from the full shape), so winding/orientation derive from a
 * non-degenerate shape and the taper collapses to clean degenerate-dropped tris.
 * The four cases compose from one ordinate f(s) (kernel wallEndArcZFt — 0 at
 * ends, rise h at mid-span):
 *   top    archConvex   top surface = capBase + f(s)          (apex mid = nominal top)
 *   top    archConcave  top surface = capBase + h − min(f, h−web)  (ends full, dips mid)
 *   bottom archConcave  bottom      = zBase + min(f, h−web)   (the ARCH underside)
 *   bottom archConvex   bottom      = zBase + (h − f(s))      (belly hangs mid-span)
 * Edges emit only along the weighted (arc) side — the flat junction to the prism
 * stays seam-free (suppressed-ring territory).
 */
function wallArcCapMesh(
  centerline: Pt2[], thicknessFt: number,
  kind: 'archConvex' | 'archConcave', riseFt: number, mode: 'top' | 'bottom', zAnchor: number,
): { mesh: { positions: Float32Array; normals: Float32Array; indices: Uint32Array; edges: number[] }; bandFt: number } | null {
  let L = 0
  for (let i = 0; i + 1 < centerline.length; i++) {
    L += Math.hypot(centerline[i + 1][0] - centerline[i][0], centerline[i + 1][1] - centerline[i][1])
  }
  if (L < 0.5) return null
  const h = wallEndArcRiseFt(riseFt, L)
  const web = Math.min(0.02, h * 0.1)
  const t2 = thicknessFt / 2
  // full mid-span band rectangle; the weighted side rides zOfS (≤ 0, reducing)
  const profile: Pt2[] = [[-t2, 0], [t2, 0], [t2, h], [-t2, h]]
  const zWeights = mode === 'top' ? [0, 0, 1, 1] : [1, 1, 0, 0]
  const zOfS = (s: number): number => {
    const fs = wallEndArcZFt(h, s, L)
    if (mode === 'top') {
      // offsets the TOP verts down from the full band height h
      return kind === 'archConvex' ? fs - h : -Math.min(fs, h - web)
    }
    // offsets the BOTTOM verts up from the band base 0
    return kind === 'archConcave' ? Math.min(fs, h - web) : h - fs
  }
  const swept = sweepProfileAlong(profile, centerline, false, zAnchor + (mode === 'top' ? -h : 0), {
    zOfS, zWeights, stationStepFt: Math.min(Math.max(L / 64, 0.25), 1),
  })
  if (!swept) return null
  return {
    mesh: { positions: swept.positions, normals: swept.normals, indices: swept.indices, edges: swept.edges },
    bandFt: h,
  }
}

export interface SolidPiece {
  profile: Pt2[]           // (s,z) elevation profile, s LOCAL to the piece's frame
  frame: ProfileFrame
  thicknessFt: number
  /**
   * Per profile edge (vertex i → i+1): draw its 3D line work or not (2026-07-31,
   * Matt's opening-seam report). A header/sill piece is FLUSH with the wall face —
   * only its opening-boundary edge is a real feature line; the sides ran phantom
   * jamb verticals up the face and the outer edge duplicated the wall silhouette.
   * Omitted = draw everything (opening protos keep their full line work).
   */
  edgeMask?: boolean[]
}

export interface OpeningInstance {
  opening: ResolvedElement
  pos: [number, number, number]   // world, at the opening base center
  rotZ: number
  /** Tier 2 corner sliders only: the leg-B chirality ('L' = +y, 'R' = −y in the
   *  proto frame) — suffixes the proto cache key in pipeline + section, so the two
   *  chirality prototypes coexist per type. pos/rotZ then anchor the CORNER, with
   *  local +x = wall A's inward direction. */
  variant?: 'L' | 'R'
}

/** one welded piece of a casework countertop (see ElementSolid.counter) */
export interface CounterPiece {
  poly: Pt2[]; z0: number; z1: number; materialId: string; depthFt: number; levelId: string
  /** 'slab' (default — sinks cut it) | 'apron' | 'waterfall' | 'splash' */
  kind?: string
  /** draws its outline in plan (default true) */
  outline?: boolean
  /** TRUE EDGES ('edgeFull' / 'edgeIn' pieces): the edge profile and the slab thickness */
  edge?: string
  thickness?: number
}

export interface ElementSolid {
  elementId: string
  /** ATMOSPHERE VIEW Stage 3: the element's texture shift/rotate (kernel
   *  texturePlacement.ts), applied to this solid's surface coordinates in
   *  buildGroupChunk. Absent = world-feet tiling unchanged. */
  uvXform?: { du: number; dv: number; rotDeg: number }
  category: import('@aj/bim-kernel').CategoryId
  outlines: Pt2[][]
  zBase: number
  zTop: number
  /**
   * PLAN-CUT VISIBILITY fix (2026-08-17, Matt's Forsyth report): wall-only, set
   * when a stepped (deriveSteppedBase) or attached (deriveAttachedBase) base
   * skin reaches lower than `zBase`. `zBase`/`zTop` MUST stay the uniform
   * prism's own bounds — `extrudeOutlines`/`prismEdges` extrude the real solid
   * from them, and the skin mesh below `zBase` is already a separate, complete
   * piece of geometry (its own cap + perimeter walls) that would double up with
   * the prism if the prism extruded down to meet it. `zExtentBase`, when
   * present, is the skin's true lower reach — read ONLY by 2D cut-inclusion
   * tests (e.g. plan.ts's wall-cut filter), which must not exclude a wall just
   * because the cut height falls below its highest stepped point. Absent =
   * same as `zBase` (the ordinary, unstepped case).
   */
  zExtentBase?: number
  materialId: string
  groupKey: string
  /**
   * PHASE SPLIT round (docs/MODELER.md): set on a solid produced by splitting a
   * closed-boundary element into phase REGIONS (floor/ceiling in v1 — see
   * phaseSplit.ts#resolvePhaseRegions) instead of the element's own top-level
   * phaseCreated/phaseDemolished. Every 2D phase-status lookup normally keys off
   * `elementId` alone (one element, one status) — this FORCES a fresh resolve for
   * this one solid specifically, the same escape hatch the DEMOLISHED-OPENING
   * INFILL marker already uses (plan.ts). Multiple solids may legitimately share
   * one elementId here (this override is exactly why); the 3D phase overlay
   * (sceneStore.js) does not yet read this — v1 shows the whole element under one
   * tint there, a known follow-on.
   */
  phaseOverride?: { phaseCreated?: PhaseId; phaseDemolished?: PhaseId }
  /**
   * PHASE SPLIT round (PHASE AREAS 2026-10-30: a list — an area's border is several
   * lines): the lines between this solid's phase region and the rest of the element
   * (world XY), carried on the zone's solid only (never the region that kept the
   * parent's own tags) so a consumer emits each seam once. Drawn as its
   * own 2D component, independent of this solid's own boundary stroke, so a
   * Complete-graphics view can drop just the seam and still draw the true outer
   * boundary unbroken.
   */
  phaseSeams?: { a: Pt2; b: Pt2 }[]
  pieces: SolidPiece[]
  /** wall-only: resolved opening placements (plan symbols + instances) */
  placements: OpeningPlacement[]
  openingInstances: OpeningInstance[]
  /**
   * Wall-only, set when openings exist (r7 — Matt's report: opening slots leaked
   * full-height into every consumer). `outlines` above stay SLOTTED full-height —
   * that is what the mesh extrusion needs (header/sill pieces fill the gap) — while
   * `fullOutlines` is the UNSLOTTED body outline and `openingVoids` the true 3D void
   * boxes (plan rect × [zLow, zHigh], zHigh = head + arch rise). Consumers that are
   * height-aware use these: 3D prism EDGES (no jamb lines crossing the header), the
   * plan cut (slot only when the cut plane passes through the void), the section cut
   * (subtract the void, so headers/sills read as wall).
   */
  fullOutlines?: Pt2[][]
  /** ROOF PLAN OCCLUSION (2026-10-19): a roof's top-surface faces as plain planes
   *  (z = z0 + g·(p − origin) inside `ring`) — the top-down plan occlusion reads the
   *  roof's real height at each point instead of its highest point everywhere */
  topFaces?: { ring: Pt2[]; origin: Pt2; z0: number; g: Pt2 }[]
  openingVoids?: { rect: Pt2[]; zLow: number; zHigh: number; elementId: string }[]
  /**
   * GENERIC MESH CONTRIBUTION (Phase 3 — roofs first, stairs/railings next): a
   * prebuilt triangle mesh + its own 3D edge lines, appended to the group chunk like
   * pieces are. With `meshOnly`, the prism pipeline (outline extrusion + prism edges)
   * is skipped — `outlines` then serve the PLAN only.
   */
  mesh?: {
    positions: Float32Array; normals: Float32Array; indices: Uint32Array; edges: number[]
    /** GRAIN-ALIGNED FRAMING round: per-vertex (u,v) in world feet, computed from
     *  the member's own run direction (prism()'s memberUvOf) instead of a blind
     *  world-axis snap — set only by prism()-built framing/trim solids; absent
     *  falls back to the generic computeWorldUvs pass at buildGroupChunk. */
    uvs?: Float32Array
  }
  meshOnly?: boolean
  /**
   * DECORATIVE BOX BEAMS (2026-10-08): a member hung from its element (a ceiling's box beam
   * boards and nailer) — single honest pieces, so section and cutaway poché fill it in its own
   * material, never band it in the element's assembly layers.
   */
  decorative?: boolean
  /**
   * PAINT round (sketch bodies, 2026-09-27): the 3D-ONLY stand-ins for this solid —
   * one per (side, material), front triangles as drawn and back triangles reversed,
   * each with its own texture uvs. docMirror swaps them in for the 3D chunks only;
   * every 2D consumer (plan cut, section, HLR, DXF, cutaway caps) keeps reading
   * the one plain `mesh` above, so no back-face duplicate ever reaches a cut.
   */
  paintParts?: { materialId: string; groupKey: string; mesh: { positions: Float32Array; normals: Float32Array; indices: Uint32Array; uvs: Float32Array; edges: number[] } }[]
  /**
   * MEMBER PAINT (2026-10-05): a 3D-ONLY material for the whole solid — the Paint
   * Bucket's coat on a post, beam or roof show member. docMirror swaps it in for the
   * 3D chunk (expandPaintParts, keyed apart with a `paint=<mat>` segment); every 2D
   * consumer and the cutaway caps keep `materialId`, so cuts show what the member IS.
   */
  surfaceMaterialId?: string
  /** FINISH LAYOUT (2026-10-10): a laid floor finish's tiles / planks — the 3D pieces. Plans
   *  draw their joints ONLY on a Finish Plan (PlanCutRequest.floorPatterns, Matt's decision);
   *  every other plan pass skips them, so the floor reads as before */
  floorPattern?: boolean
  /**
   * 3D BRICK (2026-10-05): this solid is DRAWING only — elevations/plans/sections read it, the
   * 3D view drops it (expandPaintParts). The brick coursing lines: the 3D view shows the real
   * bricks instead.
   */
  noThreeD?: boolean
  /** TRUE EDGES: the countertop's smooth body (counterLoft.ts) — 3D, sections and elevations */
  counterLoft?: boolean
  /** a countertop edge-profile TIER whose counter also has its smooth body: plans only (sections
   *  and elevations draw the body's real curve, never the stepped bands) */
  loftTier?: boolean
  /**
   * ATTACH-TARGETS/CURB round (2026-08-10): a derived mesh skin continues past the
   * prism's top/bottom, so the prism's own perimeter ring there is NOT a silhouette
   * line — buildGroupChunk's prismEdges skips it (the skin draws its own outlines).
   */
  suppressTopRing?: boolean
  suppressBottomRing?: boolean
  /**
   * ATTACH CORNER round follow-up (2026-08-12): world points where this solid's
   * own outline turns a corner but a resolved 2-wall corner weld continues the
   * silhouette straight through it — buildGroupChunk's prismEdges must not draw
   * a vertical there (see edges.ts#prismEdges' `excludeAt`). Populated by
   * pipeline.ts#weldWallCornerSolids on the two walls it welds; harmless on a
   * flat corner sharing the weld's groupKey, since unionClosed already erased
   * that vertex before prismEdges ever sees it.
   */
  excludeVerticalsAt?: Pt2[]
  /**
   * FOUNDATION CORNER + STEP round (2026-08-16): z-RANGED vertical suppression —
   * at a matching outline vertex, only the [z0, z1] band is suppressed and the
   * remainder still draws. The stepped stem/footing case: two abutting step
   * prisms are continuous over their SHARED band (no line there) but the riser
   * below/above it is a real edge that must survive. Point-only excludeVerticalsAt
   * can't express that (the known "verticals span a solid's FULL band" limit).
   */
  excludeVerticalRangesAt?: { p: Pt2; z0: number; z1: number }[]
  /**
   * FOUNDATION CORNER + STEP round (2026-08-16): top/bottom ring SEGMENT
   * suppression — ring portions collinear with an exclusion segment at that
   * ring's z are dropped. The stepped-footing lap case: where the lower
   * footing's clamped lap piece meets the higher footing's underside, both
   * pieces' rings run along a surface the other piece continues past.
   */
  excludeRingSegsAt?: { z: number; a: Pt2; b: Pt2 }[]
  /**
   * Plan linework with ELEVATIONS (Matt's drafting-correctness audit): each entry
   * draws on the element's own level's plan, classified against the cut plane —
   * solid ('visible') below it, dashed ('projected') above, and a 2-POINT segment
   * whose z0→z1 crosses the plane is SPLIT at the crossing. Multi-point polylines
   * must be flat (z1 omitted → z0 throughout).
   */
  projLines?: { pts: Pt2[]; z0: number; z1?: number }[]
  /**
   * Casework-only: this element's counter slab contribution (world-space footprint +
   * z band). NEVER rendered directly — buildSolids' counter-join post-pass buckets
   * these by weld criteria (level, z band, depth, material), unions the footprints
   * (the 1" overhangs overlap between abutting/corner units, so touching runs weld),
   * and emits one synthetic prism solid per continuous top. Derived, nothing stored.
   */
  counter?: CounterPiece
  /** CASEWORK STUDIO stage 4: the rest of this box's countertop — further edge-profile tiers,
   *  a mitered apron, waterfall ends, the backsplash (casework/generate3d.ts#counterPiecesFor) */
  counterPieces?: CounterPiece[]
  /** CASEWORK STUDIO: solids sharing this key cut in sections as ONE union outline (a
   *  countertop's stacked edge-profile tiers — the stem/footing grouped pass, section.ts) */
  cutUnionKey?: string
  /** PLUMBING STYLES (2026-10-07): the opening an undermount / apron sink cuts in the
   *  welded countertop it's seated in (world plan, feet) */
  counterCut?: { poly: Pt2[]; levelId: string }
  /**
   * This solid carries its OWN drafting-classified plan linework (projLines), so the
   * plan cut's generic dashed below-cut outline pass must skip it (a counter below
   * the cut plane reads as SOLID light lines, not a dashed overhead outline).
   */
  skipProjectedOutline?: boolean
  /**
   * WALL FRAMING round follow-up (Matt's report: doors/windows stopped reading cut
   * out in plan on framed walls): a derived FRAMING solid — a SECOND solid of
   * category 'wall' carrying the SAME elementId as its body. Consumers that assume
   * one wall solid per element must skip these: the plan's whole wall pass (its
   * per-element cutOutlines map got overwritten by the framing solid's empty
   * outlines, and the framing pass re-painted the layer-band fills WITHOUT the
   * opening voids), and the 3D corner-weld end collection (duplicate end arms made
   * a plain 2-wall corner count as a 3+ node, silently killing the weld). Sections/
   * caps need no flag — their band passes are already gated on !meshOnly, and the
   * mesh channel is exactly how framing is MEANT to appear there.
   */
  framing?: boolean
  /** EXPOSED LUMBER (2026-10-13, Matt): a roof's rafter tails, outlookers, barge rafters and show
   *  members — not framing (they draw in 2D), but lumber: cut end-on in a section they carry
   *  the drafting X like framing does */
  lumber?: boolean
  /** ROOF FRAMING ENVELOPE (2026-10-14, roof/envelope.ts): on a roof's framing-study solid,
   *  the region its rafters / tails / corbels / soffit lookouts sweep — what the section's
   *  assembly-depth tone draws, so the tone follows the real eave/rake framing */
  roofEnvelope?: import('../roof/envelope').RoofEnvelopePiece[]
  /** FRAMED ASSEMBLIES SHOW MEMBERS (2026-09-29): a floor/ceiling body split around
   *  its joist band keeps the FULL assembly's top z here, so layer banding (cutaway
   *  caps) still counts the layer stack from the true top, not from this part's */
  layerTopZ?: number
  /**
   * BARREL-TILE ROOFING (docs/bim-plan/12): a derived roofing solid (tile field/
   * caps/mortar/valley metal — categories/roof/tiles.ts). Its mesh.edges are the
   * tile OUTLINES, wanted in 3D but skipped by section.ts's elevation wireframe
   * pass (hidden-line over thousands of tiles would be prohibitive; elevation
   * coursing is a recorded later slice).
   */
  roofing?: boolean
  /** ROOF LAYERS BY PLANE (2026-11-02): a roof's roofing pieces, grouped by their planes' scope —
   *  the roofing verb those planes carry (scope.ts reads it in place of the whole roof's) and
   *  whether new roofing was troweled onto them. Unset on roofs with no scope. */
  scopeVerb?: string
  scopeAdded?: boolean
  /**
   * WALL AUTO SKIN round (2026-08-13, Matt: "Gypsum board is set to white but it
   * looks very tan in the model"): a derived FINISH-LAYER skin — a SECOND (or
   * third) solid of category 'wall' carrying the SAME elementId as its body, a
   * thin painted overlay on one face classified from the wall's own extruded
   * triangles (see wallFinishSkins). Same "consumers that assume one wall solid
   * per element must skip these" contract as `framing` above, and the same two
   * spots: the plan's whole-wall pass and the 3D corner-weld end collection.
   * meshOnly + skipProjectedOutline already keep it out of everything else
   * (section/HLR/vertical-pick gate on !meshOnly generically) — EXCEPT the
   * section per-layer poché band pass, which needs its own explicit skip too
   * (it reads `el.assembly` directly by elementId, not gated on meshOnly).
   */
  skin?: boolean
  /**
   * FINISH SKIRT round (2026-08-16, Matt's exposed-band report): the exterior
   * finish + sheathing extended DOWN past the wall body's derived base — across
   * the floor band to the top of concrete by default, or (finishToGrade) over
   * the exposed concrete to a 4" clearance above the grade line. A SECOND solid
   * of category 'wall' with the wall's own elementId, so it inherits the same
   * "consumers that assume one wall solid per element must skip these" contract
   * as `framing`/`skin` (plan's wall pass, the corner-weld end collection, the
   * section per-layer band pass). UNLIKE a paint `skin` it is real geometry:
   * it occludes, section-cuts (generic finish-material poché), and draws in
   * elevations — the screed line at its bottom edge is elevation-critical.
   */
  skirt?: boolean
  /** WALL END FINISHES (2026-10-06): a free wall end's finish cap (endFinish.ts) — a `skirt`
   *  piece the plan's crossing-cut pass still draws, in its own material */
  endCap?: boolean
  /**
   * FINISH FACE (2026-10-04): the triangles of this piece that ARE a wall's visible
   * finished face (a skirt's outer face, a split-band strip's front) — what the
   * surface-detail pass (finishFaces.ts) lays siding joints and battens across, next
   * to the finish skins. Skins need none: every skin triangle is finish face.
   */
  finishFace?: { positions: Float32Array; normals: Float32Array; indices: Uint32Array }
  /**
   * Synthetic 2-wall corner-weld prism (buildSolids' weld post-pass, 2026-07-30 field
   * fix): the plan corner patch extruded over the walls' shared z-range so unjoined
   * corners read welded in 3D/section like they do in plan. Consumers with their OWN
   * corner handling must skip these: the plan cut excludes them from its wall pass
   * entirely (it derives the patch itself), and the section's per-layer wall fills
   * skip them (elementId points at a real wall whose bands its own solid already
   * emitted).
   */
  cornerWeld?: boolean
  /**
   * Synthetic join solids only: every element whose edit must regenerate this solid's
   * group — the counter join's member cabinets, the corner weld's two walls. The doc
   * mirror's dirty tracking reads this (defaulting to [elementId]); without it, moving
   * a non-representative contributor left the joined chunk stale.
   */
  contributorIds?: string[]
  /**
   * CURVES round: candidate source circles for this solid's `outlines` — a bulge-free
   * element leaves this unset (flat-shaded exactly as before). When set, the group
   * chunk's extrusion (pipeline.ts#buildGroupChunk) smooth-shades whichever facet runs
   * classify as a genuine ≥2-segment arc on one of these circles (extrude.ts), so a
   * curved wall/slab edge reads as a smooth cylinder instead of a faceted polygon —
   * real corners, miters, and straight runs keep their hard flat-shaded edges exactly
   * as every other element always has.
   */
  circles?: import('../../arcs2d').ArcCircle[]
  /**
   * TERRAIN EDGE GUARD (2026-08-23, topo-only): an invisible, DEEP collision-only
   * skirt hanging from this terrain solid's own boundary hull — riding as inert
   * metadata (never its own ElementSolid/`category:'topo'` entry — that leaked
   * into every OTHER `category === 'topo'` 2D consumer, which filter at the
   * resolved-ELEMENT level and are blind to `skipProjectedOutline`). No 2D
   * consumer reads this field; `buildGroupChunk` (pipeline.ts) copies it onto the
   * resulting GroupChunk, and sceneStore.js alone reads `chunk.walkSkirt` to
   * build the invisible collider for walk-mode. See topo/generate3d.ts#topoSolid.
   */
  walkSkirt?: { positions: Float32Array; indices: Uint32Array }
  /** VINES (2026-10-05): the grown stems + leaves the viewer draws over this solid's
   *  invisible coverage mesh (categories/vine/generate3d.ts#VineDraw) */
  vine?: { species: string; stems: Float32Array; leaves: Float32Array }
}

const SLOT_EPS = 0.02 // slot rects extend past the wall faces so the boolean is never coplanar

/**
 * WALL NICHE (2026-10-07): where one niche cuts its host — the rough opening along the
 * wall (centered on its offset, held on one straight segment like any opening), the
 * plan rect of the recess (from just past the niche's face to its rough depth), and
 * the across-wall center + depth of the fill pieces above/below it. Null = nothing to
 * cut (no spec, no depth, or it doesn't fit the segment).
 */
export function nicheRecess(
  niche: ResolvedElement, frame: WallFrame, t: number, delta: number, zWallBase: number,
): { placement: OpeningPlacement; rect: Pt2[]; across: number; depth: number } | null {
  const ns = niche.nicheSpec
  if (!ns || ns.depthFt <= 1e-6 || ns.roughDepthFt >= t - 1e-6) return null
  const clamped = frame.clampToSegment((niche.params.hostOffsetFt as number) || 0, ns.roughWFt)
  if (!clamped) return null
  const zLvl = niche.level?.elevationFt ?? zWallBase
  const placement: OpeningPlacement = {
    elementId: niche.id,
    s0: clamped.center - ns.roughWFt / 2,
    s1: clamped.center + ns.roughWFt / 2,
    center: clamped.center,
    zLow: zLvl + ns.sillFt - ns.liningFt,
    zHead: zLvl + ns.sillFt + ns.clearHFt + ns.liningFt,
    riseFt: 0,
    rakeFt: 0,
    widthFt: ns.roughWFt,
  }
  const a = frame.pointAt(placement.center)
  const n: Pt2 = [-a.dir[1], a.dir[0]]
  const p0 = frame.pointAt(placement.s0).pt, p1 = frame.pointAt(placement.s1).pt
  // across-wall offsets from the CENTERLINE: the face (pushed past it) → rough depth
  const outer = ns.sign * (t / 2 + SLOT_EPS)
  const inner = ns.sign * (t / 2 - ns.roughDepthFt)
  const at = (p: Pt2, off: number): Pt2 => [p[0] + n[0] * (delta + off), p[1] + n[1] * (delta + off)]
  const rect = [at(p0, outer), at(p1, outer), at(p1, inner), at(p0, inner)]
  return {
    placement, rect,
    across: ns.sign * (t / 2 - ns.roughDepthFt / 2),
    depth: ns.roughDepthFt,
  }
}

/**
 * `deltaFt` (2.6a location lines): the wall BODY is centered on the derived centerline,
 * `deltaFt` along the left normal from the stored reference path the frame runs on — the
 * slot shifts with it so the cut always passes through the body, not the reference line.
 */
/** POCKET DOORS round: a plan rect along the wall between stations s0..s1, spanning
 *  [lo, hi] across it (offsets from the CENTERLINE), continued straight past the wall's
 *  ends (pointAtExt) — the pocket channel, the furred housing, the widened slot */
export function acrossRect(f: WallFrame, s0: number, s1: number, lo: number, hi: number, deltaFt: number): Pt2[] {
  const a = f.pointAtExt((s0 + s1) / 2)
  const n: Pt2 = [-a.dir[1], a.dir[0]]
  const p0 = f.pointAtExt(s0).pt, p1 = f.pointAtExt(s1).pt
  const at = (p: Pt2, off: number): Pt2 => [p[0] + n[0] * (deltaFt + off), p[1] + n[1] * (deltaFt + off)]
  return [at(p0, hi), at(p1, hi), at(p1, lo), at(p0, lo)]
}

export function slotRect(f: WallFrame, s0: number, s1: number, halfT: number, deltaFt: number): Pt2[] {
  const a = f.pointAt((s0 + s1) / 2)
  const n: Pt2 = [-a.dir[1], a.dir[0]]
  const c: Pt2 = [n[0] * deltaFt, n[1] * deltaFt]
  const p0 = f.pointAt(s0).pt, p1 = f.pointAt(s1).pt
  const h = halfT + SLOT_EPS
  return [
    [p0[0] + c[0] + n[0] * h, p0[1] + c[1] + n[1] * h],
    [p1[0] + c[0] + n[0] * h, p1[1] + c[1] + n[1] * h],
    [p1[0] + c[0] - n[0] * h, p1[1] + c[1] - n[1] * h],
    [p0[0] + c[0] - n[0] * h, p0[1] + c[1] - n[1] * h],
  ]
}

// ---------- walls-attach (ROOF round, 2026-08; ATTACH-TARGETS round, 2026-08-10) ----------
// An attached wall derives its top by clipping against its TARGET elements' surfaces
// at generation time — roofs' UNDERSIDE (bearing) planes and floors'/ceilings'
// undersides — no stored geometry, no constraint engine; it re-derives whenever a
// target changes (docMirror expands target dirt onto attached walls) and detaches by
// clearing the params. Targets are the PICKED elements in `topAttachIds` (the Revit
// attach model); the legacy `topAttach: 'roof'` still means every overlapping roof.
// The body stays a PRISM up to the LOWEST clip point over the footprint (so the
// openings/header/sill machinery is untouched), and a mesh top fills from there to
// the clip envelope — raked tops in section, gable walls filling their gable. Where
// the footprint pokes out past every target, the top caps flat at the nominal
// unconnected height.
//
// OPENINGS IN THE SKIN (2026-08-10, Matt's filled-in-windows report): an opening
// whose head rises above the prism top used to get meshed over. The skin now splits
// at every such opening's plan rect: a HEADER piece above the void (bottom at the
// void top, underside capped) and, for a void floating fully above the prism top, a
// SILL piece below it — with jamb step faces between differing piece bottoms, so the
// void reads as a true hole.
//
// OUTLINES (2026-08-10, same round): the skin now draws its full silhouette — piece
// top lines (as before), verticals where the skin's PERIMETER turns a plan corner
// (wall ends), and bottom-cap outlines — and the prism's own top ring is suppressed
// (suppressTopRing) so the extended wall reads as ONE wall, not a wall with an
// unlined cap floating on it.
// v1 limits (docs/MODELER.md): overlapping targets resolve first-target-wins per
// plan region; layered poché above the prism top follows the roof-poché open item.

interface TopPiece {
  ring: Pt2[]
  zAt: (p: Pt2) => number
  /** where this piece's skin stops (bands run down to here) — the prism top, or an
   *  opening void's top for the header piece over it */
  bottom: number
  /** header pieces get a DOWN-facing cap at `bottom` (the visible underside above
   *  an opening) plus its outline */
  bottomCap?: boolean
}

const ATTACH_MIN_HEIGHT = 0.1

function bboxOf(rings: Pt2[][]): [number, number, number, number] {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
  for (const r of rings) {
    for (const p of r) {
      if (p[0] < x0) x0 = p[0]
      if (p[1] < y0) y0 = p[1]
      if (p[0] > x1) x1 = p[0]
      if (p[1] > y1) y1 = p[1]
    }
  }
  return [x0, y0, x1, y1]
}

const bboxOverlap = (a: [number, number, number, number], b: [number, number, number, number]) =>
  a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3]

const keyPt = (p: Pt2) => `${Math.round(p[0] * 1e5)},${Math.round(p[1] * 1e5)}`
const edgeKeyOf = (a: Pt2, b: Pt2) => {
  const ka = keyPt(a), kb = keyPt(b)
  return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`
}

/** shared flat-shaded triangle accumulator for the derived top/base skins */
class SkinAcc {
  pos: number[] = []
  nrm: number[] = []
  idx: number[] = []
  edges: number[] = []
  private vc = 0
  tri(a: number[], b: number[], c: number[]): void {
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2]
    const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2]
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
    const len = Math.hypot(nx, ny, nz) || 1
    nx /= len; ny /= len; nz /= len
    this.pos.push(...a, ...b, ...c)
    this.nrm.push(nx, ny, nz, nx, ny, nz, nx, ny, nz)
    this.idx.push(this.vc, this.vc + 1, this.vc + 2)
    this.vc += 3
  }
  build(): NonNullable<ElementSolid['mesh']> {
    return {
      positions: new Float32Array(this.pos),
      normals: new Float32Array(this.nrm),
      indices: new Uint32Array(this.idx),
      edges: this.edges,
    }
  }
  get empty(): boolean { return this.idx.length === 0 }
}

/** concatenate two derived skins into one mesh contribution */
export function mergeMeshes(
  a: NonNullable<ElementSolid['mesh']>,
  b: NonNullable<ElementSolid['mesh']>,
): NonNullable<ElementSolid['mesh']> {
  const positions = new Float32Array(a.positions.length + b.positions.length)
  positions.set(a.positions, 0); positions.set(b.positions, a.positions.length)
  const normals = new Float32Array(a.normals.length + b.normals.length)
  normals.set(a.normals, 0); normals.set(b.normals, a.normals.length)
  const indices = new Uint32Array(a.indices.length + b.indices.length)
  indices.set(a.indices, 0)
  const base = a.positions.length / 3
  for (let i = 0; i < b.indices.length; i++) indices[a.indices.length + i] = b.indices[i] + base
  return { positions, normals, indices, edges: [...a.edges, ...b.edges] }
}

/** an attach target's clip surfaces: roof undersides, or a slab's underside */
function targetSurfaces(target: ResolvedElement): { rings: Pt2[][]; zAt: (p: Pt2) => number }[] {
  if (target.category === 'roof') {
    // ROOF ROUND E3 (the E1 reveal fix): clip against the roof's ACTUAL DERIVED
    // underside — zone-split at the bearing polygon by the ONE evaluator the roof's
    // own mesh renders from (underside.ts), never a re-derivation. The share of a
    // wall's thickness under a thinned eave rises to the raised plane, and the
    // piece-step machinery below draws the plumb face between the shares. An
    // uncropped face yields exactly the pre-E3 single surface, so clipping under
    // the BODY zone is unchanged (E1's invariant, softened to the body zone).
    return roofUndersideSurfaces(target)
  }
  if (target.category === 'floor' || target.category === 'ceiling') {
    const s = slabSolid(target)
    if (!s) return []
    const sl = slopedCeilingZ(target)   // SLOPED CEILING round: the tilted underside
    if (sl) return [{ rings: s.outlines, zAt: sl.under }]
    const z = s.zBase   // the slab UNDERSIDE — where an attached wall top lands
    return [{ rings: s.outlines, zAt: () => z }]
  }
  return []
}

/**
 * PLATE-HEIGHT round (2026-08-13): the plate rule's view of an attach target —
 * roof faces carry their MAIN BODY plane + bearing metadata (roofPlateFaces, so
 * the E1 eave crop never bends a plate); a slab target is a level plate directly
 * under the slab's own FRAMING when it's a wood floor (joist undersides — the
 * platform-framing read; the gyp below the joists dies into the wall), else its
 * mass underside.
 */
function plateSurfsFor(target: ResolvedElement): PlateSurf[] {
  if (target.category === 'roof') {
    return roofPlateFaces(target).map((f) => ({ kind: 'roof' as const, ...f }))
  }
  if (target.category === 'floor' || target.category === 'ceiling') {
    const s = slabSolid(target)
    if (!s) return []
    if (slopedCeilingZ(target)) return []   // a tilted ceiling has no level plate (v1)
    const rise = target.category === 'floor' && isWoodCoreFloor(target)
      ? assemblyZonesFt(target).belowFt : 0
    return [{ kind: 'flat' as const, rings: s.outlines, z: s.zBase + rise }]
  }
  return []
}

/**
 * BASE ATTACH round (2026-08-10): the mirror of `targetSurfaces` — a target's TOP
 * (walking/weather) surface, where an attached wall's BASE lands. Roofs: the bearing
 * (underside) plane plus assembly thickness stacked UP (the same plumb-thickness math
 * generate3d.ts uses to draw the roof's own top cap — `t·√(1+m²)`, never re-derived).
 * Floors/ceilings: the slab's own zTop (its finish surface either way).
 */
function targetTopSurfaces(target: ResolvedElement): { rings: Pt2[][]; zAt: (p: Pt2) => number; underAt?: (p: Pt2) => number }[] {
  if (target.category === 'roof') {
    const solved = solveRoofForElement(target)
    if (!solved || !solved.faces.length) return []
    const thk = target.assemblyThicknessFt
    return solved.faces.map((f) => {
      const dv = thk * Math.sqrt(1 + f.m * f.m)
      // underAt (TEAR r8): the sheet's BOTTOM plane — where a wall sliver just
      // past this sheet's cut edge stops, covering the edge face from inside
      return { rings: [f.ring], zAt: (p: Pt2) => f.zAt(p) + dv, underAt: (p: Pt2) => f.zAt(p) }
    })
  }
  if (target.category === 'floor' || target.category === 'ceiling') {
    const s = slabSolid(target)
    if (!s) return []
    const sl = slopedCeilingZ(target)
    if (sl) return [{ rings: s.outlines, zAt: sl.top, underAt: sl.under }]
    const z = s.zTop   // the slab's own TOP surface — where an attached wall base lands
    return [{ rings: s.outlines, zAt: () => z, underAt: () => s.zBase }]
  }
  return []
}

/** plan distance from p to the nearest segment of any ring in `rings` */
function distToRings(p: Pt2, rings: Pt2[][]): number {
  let best = Infinity
  for (const r of rings) {
    for (let i = 0; i < r.length; i++) {
      const a = r[i], b = r[(i + 1) % r.length]
      const dx = b[0] - a[0], dy = b[1] - a[1]
      const len2 = dx * dx + dy * dy
      const t = len2 < 1e-12 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2))
      best = Math.min(best, Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t)))
    }
  }
  return best
}

function pointInRingW(p: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j]
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}
const ringMid = (r: Pt2[]): Pt2 => {
  let x = 0, y = 0
  for (const p of r) { x += p[0]; y += p[1] }
  return [x / r.length, y / r.length]
}
/** a representative point guaranteed INSIDE the ring — the centroid of its
 *  largest earcut triangle. The plain vertex average (`ringMid`) lands OUTSIDE
 *  a non-convex ring — a CHAINED wall's claim against one clip sheet is an
 *  L-shaped region spanning two path segments, whose average sits back in the
 *  roof's main body zone where the knee pair resolves against a DIFFERENT
 *  sheet, so the whole (correct) claim was rejected and that stretch fell to
 *  the nominal height (Matt's 2026-08-19 gable report: outer layers stopped
 *  short of the rake underside, studs exposed — only when walls were chained). */
const ringInteriorPt = (r: Pt2[]): Pt2 => {
  const tris = earcut(r.flatMap((p) => [p[0], p[1]]))
  let best: Pt2 | null = null
  let bestA = 0
  for (let i = 0; i < tris.length; i += 3) {
    const a = r[tris[i]], b = r[tris[i + 1]], c = r[tris[i + 2]]
    const area = Math.abs((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]))
    if (area > bestA) { bestA = area; best = [(a[0] + b[0] + c[0]) / 3, (a[1] + b[1] + c[1]) / 3] }
  }
  return best ?? ringMid(r)
}

/**
 * TEAR r7 — the knee-wall surface pairing, shared by BOTH attach derivations.
 * A wall carrying top AND base attaches on a torn roof stands between STACKED
 * sheets: several floor surfaces (the targets' tops) and several ceiling
 * surfaces (the targets' undersides) cover the same plan point. The wall's
 * stretch there is the HIGHEST floor that still has a ceiling above it, up to
 * the LOWEST such ceiling — deterministic from the two surface lists alone, so
 * the top and base derivations agree without consulting each other's output.
 */
export interface KneeSurfaces {
  floors: { rings: Pt2[][]; zAt: (p: Pt2) => number; underAt?: (p: Pt2) => number }[]
  ceils: { rings: Pt2[][]; zAt: (p: Pt2) => number }[]
}
/** a real knee stretch is at least this tall — thinner "gaps" are the two
 *  sheets CROSSING near a ridge (surface slivers), and pairing onto one would
 *  pin the wall into a fin through the roof; below this, the region falls back
 *  to the ordinary one-sided attach behavior */
const KNEE_MIN_GAP = 1.0
function kneePairAt(knee: KneeSurfaces, p: Pt2): { floor: number; ceil: number } | null {
  const fzs: number[] = []
  const czs: number[] = []
  for (const s of knee.floors) for (const r of s.rings) { if (pointInRingW(p, r)) { fzs.push(s.zAt(p)); break } }
  for (const s of knee.ceils) for (const r of s.rings) { if (pointInRingW(p, r)) { czs.push(s.zAt(p)); break } }
  let best: { floor: number; ceil: number } | null = null
  for (const f of fzs) {
    let c: number | null = null
    for (const cz of czs) if (cz >= f + KNEE_MIN_GAP && (c === null || cz < c)) c = cz
    if (c !== null && (!best || f > best.floor)) best = { floor: f, ceil: c }
  }
  return best
}

export function deriveAttachedTop(
  bodyOutlines: Pt2[][],
  zBase: number,
  zTopNominal: number,
  targets: ResolvedElement[],
  voids: { rect: Pt2[]; zLow: number; zHigh: number }[],
  // TEAR r7: both attach sides' surfaces, passed ONLY when the wall carries
  // both attaches (the knee wall on one torn roof) — claims resolve to the
  // paired stretch (kneePairAt), never to a sheet under the wall
  knee?: KneeSurfaces,
  // FRAMING SESSION Phase 3: pre-built clip-surface sets appended AFTER the
  // targets' own (first-target-wins order preserved) — the wallIsStem
  // floor-bearing surface rides in here (a wood floor's rings at the mudsill
  // seat elevation), since it has no target ELEMENT semantics of its own
  extraSurfSets: { rings: Pt2[][]; zAt: (p: Pt2) => number }[][] = [],
): { prismTop: number; mesh: NonNullable<ElementSolid['mesh']> | null } | null {
  const wallBox = bboxOf(bodyOutlines)
  let pieces: TopPiece[] = []
  let remaining = bodyOutlines
  for (const surfSet of [...targets.map((t) => targetSurfaces(t)), ...extraSurfSets]) {
    if (!remaining.length) break
    for (const surf of surfSet) {
      if (!remaining.length) break
      if (!bboxOverlap(wallBox, bboxOf(surf.rings))) continue
      const allRings = intersectClosed(remaining, surf.rings).filter((r) => r.length >= 3)
      // TEAR r6 (the auto knee-wall fill): a surface lying BELOW the wall's
      // base can never be its top — a torn roof stacks sheets (the flying rake
      // over the lower roof), and a knee wall seated on the LOWER sheet must
      // rise to the strip above it, not flatten against the sheet it stands
      // on. Skipped regions stay claimable by the later, higher surface.
      // r7: a both-attached wall resolves each region to its PAIRED stretch —
      // the claim must be the pair's ceiling (regions with no stacked pair
      // keep ordinary first-claim behavior).
      const rings = allRings.filter((r) => {
        if (!r.some((p) => surf.zAt(p) > zBase + ATTACH_MIN_HEIGHT)) return false
        if (!knee) return true
        const mid = ringInteriorPt(r)
        const pair = kneePairAt(knee, mid)
        return !pair || Math.abs(surf.zAt(mid) - pair.ceil) <= 1e-6
      })
      for (const ring of rings) pieces.push({ ring, zAt: surf.zAt, bottom: 0 })
      if (rings.length === allRings.length) {
        if (rings.length) remaining = differenceClosed(remaining, surf.rings).filter((r) => r.length >= 3)
      } else if (rings.length) {
        remaining = differenceClosed(remaining, rings).filter((r) => r.length >= 3)
      }
    }
  }
  if (!pieces.length) return null   // nothing overhead — the attach derives to a plain wall
  for (const ring of remaining) pieces.push({ ring, zAt: () => zTopNominal, bottom: 0 })

  let prismTop = Infinity
  for (const piece of pieces) for (const p of piece.ring) prismTop = Math.min(prismTop, piece.zAt(p))
  prismTop = Math.max(prismTop, zBase + ATTACH_MIN_HEIGHT)
  for (const piece of pieces) piece.bottom = prismTop
  const zOf = (piece: TopPiece, p: Pt2) => Math.max(piece.zAt(p), piece.bottom)

  let zPeak = -Infinity
  for (const piece of pieces) for (const p of piece.ring) zPeak = Math.max(zPeak, zOf(piece, p))
  if (zPeak <= prismTop + 1e-6) return { prismTop, mesh: null }   // flat attach — prism alone

  // opening voids reaching into the skin: split the covering pieces so the void
  // stays a HOLE — a header piece above it (underside capped) and, for a void
  // floating fully above the prism top, a sill piece below it
  for (const v of voids) {
    if (v.zHigh <= prismTop + 1e-6) continue
    const next: TopPiece[] = []
    for (const piece of pieces) {
      const inter = intersectClosed([piece.ring], [v.rect]).filter((r) => r.length >= 3)
      if (!inter.length) { next.push(piece); continue }
      for (const r of differenceClosed([piece.ring], [v.rect]).filter((rr) => rr.length >= 3)) {
        next.push({ ...piece, ring: r })
      }
      for (const r of inter) {
        next.push({ ring: r, zAt: piece.zAt, bottom: Math.max(piece.bottom, v.zHigh), bottomCap: true })
        if (v.zLow > piece.bottom + 1e-6) {
          const zl = Math.max(piece.bottom, Math.min(v.zLow, zPeak))
          next.push({ ring: r, zAt: () => zl, bottom: piece.bottom })
        }
      }
    }
    pieces = next
  }
  // drop fully-degenerate pieces (an opening head poking above the clip surface —
  // there is genuinely no wall there)
  pieces = pieces.filter((p) => p.ring.length >= 3
    && (p.ring.some((pt) => p.zAt(pt) > p.bottom + 1e-6) || false))

  const acc = new SkinAcc()

  // normalize rings CCW so the band winding matches the roof generator's convention
  const ccw = pieces.map((piece) => ({
    ...piece,
    ring: signedArea(piece.ring) < 0 ? [...piece.ring].reverse() : piece.ring,
  }))

  // edge census across pieces: shared creases (equal tops) need no band; a step
  // between pieces gets a face spanning the top gap, and differing BOTTOMS (an
  // opening's jamb line) get a face spanning the bottom gap
  interface CensusRec { a: Pt2; b: Pt2; tops: [number, number][]; bottoms: number[] }
  const census = new Map<string, CensusRec>()
  for (const piece of ccw) {
    for (let i = 0; i < piece.ring.length; i++) {
      const a = piece.ring[i], b = piece.ring[(i + 1) % piece.ring.length]
      const k = edgeKeyOf(a, b)
      const za = zOf(piece, a), zb = zOf(piece, b)
      const rec = census.get(k)
      // store tops keyed to the CANONICAL end order so two pieces' entries compare
      const canon: [number, number] = keyPt(a) < keyPt(b) ? [za, zb] : [zb, za]
      if (rec) { rec.tops.push(canon); rec.bottoms.push(piece.bottom) }
      else {
        census.set(k, {
          a: keyPt(a) < keyPt(b) ? a : b, b: keyPt(a) < keyPt(b) ? b : a,
          tops: [canon], bottoms: [piece.bottom],
        })
      }
    }
  }

  // perimeter-corner verticals (the extended-extents outline fix): collect the
  // UNSHARED (perimeter) edges per endpoint; a vertex where the perimeter turns a
  // plan corner gets a vertical from the skin top down to the piece bottom. A vertex
  // where two collinear perimeter edges meet (a crease between pieces mid-face) draws
  // nothing — the face is flat there.
  const cornerCand = new Map<string, { p: Pt2; dirs: Pt2[]; top: number; bottom: number }>()

  for (const piece of ccw) {
    const top = piece.ring.map((p) => [p[0], p[1], zOf(piece, p)])
    const flatCoords = piece.ring.flatMap((p) => [p[0], p[1]])
    const tris = earcut(flatCoords)
    for (let i = 0; i < tris.length; i += 3) {
      const [a, b, c] = [tris[i], tris[i + 1], tris[i + 2]]
      const upOk = (piece.ring[b][0] - piece.ring[a][0]) * (piece.ring[c][1] - piece.ring[a][1])
        - (piece.ring[b][1] - piece.ring[a][1]) * (piece.ring[c][0] - piece.ring[a][0]) > 0
      if (upOk) acc.tri(top[a], top[b], top[c])
      else acc.tri(top[a], top[c], top[b])
      // header pieces: DOWN-facing cap at the piece bottom (the underside visible
      // through the opening top)
      if (piece.bottomCap) {
        const capA = [piece.ring[a][0], piece.ring[a][1], piece.bottom]
        const capB = [piece.ring[b][0], piece.ring[b][1], piece.bottom]
        const capC = [piece.ring[c][0], piece.ring[c][1], piece.bottom]
        if (upOk) acc.tri(capA, capC, capB)
        else acc.tri(capA, capB, capC)
      }
    }
    for (let i = 0; i < piece.ring.length; i++) {
      const j = (i + 1) % piece.ring.length
      const k = edgeKeyOf(piece.ring[i], piece.ring[j])
      const rec = census.get(k)!
      if (rec.tops.length < 2) {
        // perimeter band (down to this piece's own bottom) — the roof band pattern
        const botI = [piece.ring[i][0], piece.ring[i][1], piece.bottom]
        const botJ = [piece.ring[j][0], piece.ring[j][1], piece.bottom]
        acc.tri(top[i], botI, botJ)
        acc.tri(top[i], botJ, top[j])
        // register both endpoints as perimeter-corner candidates
        for (const [pi, zi] of [[i, top[i][2]], [j, top[j][2]]] as [number, number][]) {
          const p = piece.ring[pi]
          const key = keyPt(p)
          const q = piece.ring[pi === i ? j : i]
          const d = Math.hypot(q[0] - p[0], q[1] - p[1])
          if (d < 1e-9) continue
          const dir: Pt2 = [(q[0] - p[0]) / d, (q[1] - p[1]) / d]
          const cand = cornerCand.get(key)
          if (cand) {
            cand.dirs.push(dir)
            cand.top = Math.max(cand.top, zi)
            cand.bottom = Math.min(cand.bottom, piece.bottom)
          } else {
            cornerCand.set(key, { p, dirs: [dir], top: zi, bottom: piece.bottom })
          }
        }
      }
      // bottom-cap outline (the header underside: head + jamb lines)
      if (piece.bottomCap) {
        acc.edges.push(
          piece.ring[i][0], piece.ring[i][1], piece.bottom,
          piece.ring[j][0], piece.ring[j][1], piece.bottom,
        )
      }
    }
  }

  // shared-edge feature lines + top steps + bottom (jamb) steps, once per unique edge
  for (const rec of census.values()) {
    const [za0, zb0] = rec.tops[0]
    acc.edges.push(rec.a[0], rec.a[1], za0, rec.b[0], rec.b[1], zb0)
    if (rec.tops.length === 2) {
      const [za1, zb1] = rec.tops[1]
      if (Math.abs(za0 - za1) > 1e-6 || Math.abs(zb0 - zb1) > 1e-6) {
        // vertical step between the two tops — emit both windings so the step face
        // shows regardless of which side it's viewed from (front-side culling)
        const A0 = [rec.a[0], rec.a[1], za0], B0 = [rec.b[0], rec.b[1], zb0]
        const A1 = [rec.a[0], rec.a[1], za1], B1 = [rec.b[0], rec.b[1], zb1]
        acc.tri(A0, B0, B1); acc.tri(A0, B1, A1)
        acc.tri(A0, B1, B0); acc.tri(A0, A1, B1)
        acc.edges.push(rec.a[0], rec.a[1], za1, rec.b[0], rec.b[1], zb1)
      }
      const [bot0, bot1] = rec.bottoms
      if (Math.abs(bot0 - bot1) > 1e-6) {
        // jamb face spanning the two piece bottoms (e.g. header bottom ↔ prism top)
        const A0 = [rec.a[0], rec.a[1], bot0], B0 = [rec.b[0], rec.b[1], bot0]
        const A1 = [rec.a[0], rec.a[1], bot1], B1 = [rec.b[0], rec.b[1], bot1]
        acc.tri(A0, B0, B1); acc.tri(A0, B1, A1)
        acc.tri(A0, B1, B0); acc.tri(A0, A1, B1)
      }
    }
  }

  // emit the perimeter-corner verticals: a vertex is a real silhouette corner when
  // its perimeter edges don't continue straight through it
  for (const cand of cornerCand.values()) {
    if (cand.dirs.length < 2) continue
    let straight = true
    for (let i = 1; i < cand.dirs.length && straight; i++) {
      const cross = Math.abs(cand.dirs[0][0] * cand.dirs[i][1] - cand.dirs[0][1] * cand.dirs[i][0])
      if (cross > 0.01) straight = false
    }
    if (straight) continue
    if (cand.top - cand.bottom < 1e-6) continue
    acc.edges.push(cand.p[0], cand.p[1], cand.top, cand.p[0], cand.p[1], cand.bottom)
  }

  return { prismTop, mesh: acc.empty ? null : acc.build() }
}

// ---------- wall base attach (2026-08-10, the cheek-wall recipe) ----------
// The mirror of `deriveAttachedTop` above: a wall's BASE derives by clipping UP
// against its BASE-attach targets' TOP surfaces — a wall standing on a lower roof
// (the cheek recipe: Attach top → upper roof, Attach base → lower roof — the wedge
// derives and re-derives as either roof changes) or on a sloped floor/ceiling.
// Targets are the PICKED elements in `baseAttachIds` — no legacy blanket mode (this
// shipped with picked targets from day one). The body stays a PRISM down to the
// HIGHEST clip point over the footprint (so the prism never pokes through any
// target), and a mesh base fills from there DOWN to the clip envelope. Where the
// footprint isn't over any target, the base caps flat at the nominal level base —
// the witness-degradation convention (delete every target and the wall falls back
// to its ordinary base, never crashes).
//
// The skin triangulation reuses the DOWN-facing / OUTWARD-winding convention
// established for the stem/curb base skin below (`deriveSteppedBase`, incl. its
// 5e99257f winding fix) rather than deriveAttachedTop's up-facing cap — this mesh
// faces down onto the target, same as a stem's underside skin. Openings are NOT
// split against this skin (v1 — an opening whose sill dips below the derived base is
// a design conflict to resolve, not geometry to honor, the same standing rule the
// curb's sillJobs already defer to the final base for). `footingEnabled` and
// `baseAttachIds` are mutually exclusive in v1 (guarded in wallSolid and the pet
// palette) — a wall standing on a roof has no footing.

interface AttachBasePiece {
  ring: Pt2[]
  zAt: (p: Pt2) => number
  /** the flat reference plane this piece's skin rises to — the derived prism base */
  top: number
  /** unique per piece; identifies it for shared-edge grouping below */
  pieceId: number
  /**
   * WINDOW-OVER-SKIN fix (2026-08-17, extended to the attach-base case): set
   * ONLY on the below-sill/above-head siblings a void notch splits a single
   * piece into — shared between them so the census below recognizes them as
   * the SAME vertically-stacked column (no connecting face between them;
   * that gap IS the opening) rather than two genuinely adjacent, side-by-side
   * pieces (which DO need a connecting/riser face). Absent = this piece's own
   * identity (`pieceId`), i.e. it stands alone.
   */
  stackId?: number
}

export function deriveAttachedBase(
  bodyOutlines: Pt2[][],
  zBaseNominal: number,
  zTop: number,
  targets: ResolvedElement[],
  // TEAR r7: both attach sides' surfaces (see deriveAttachedTop) — a base
  // claim must be the paired stretch's FLOOR; regions with no stacked pair
  // fall to the nominal base
  knee?: KneeSurfaces,
  // WINDOW-OVER-SKIN fix (2026-08-17): openings hosted on a wall whose base
  // attaches to a lower target (e.g. standing on a roof) now notch out of
  // this skin too — previously left entirely uncut (the "design conflict"
  // v1 stance never actually punched anything here). Mirrors the same fix
  // in deriveSteppedBase below.
  voids: { rect: Pt2[]; zLow: number; zHigh: number }[] = [],
): { prismBase: number; trueMinZ: number; mesh: NonNullable<ElementSolid['mesh']> | null } | null {
  let nextPieceId = 0
  const wallBox = bboxOf(bodyOutlines)
  let pieces: AttachBasePiece[] = []
  let remaining = bodyOutlines
  for (const target of targets) {
    if (!remaining.length) break
    for (const surf of targetTopSurfaces(target)) {
      if (!remaining.length) break
      if (!bboxOverlap(wallBox, bboxOf(surf.rings))) continue
      const allRings = intersectClosed(remaining, surf.rings).filter((r) => r.length >= 3)
      const rings = allRings.filter((r) => {
        if (!knee) return true
        const mid = ringInteriorPt(r)
        const pair = kneePairAt(knee, mid)
        if (!pair) return false
        return Math.abs(surf.zAt(mid) - pair.floor) <= 1e-6
      })
      for (const ring of rings) pieces.push({ ring, zAt: surf.zAt, top: 0, pieceId: nextPieceId++ })
      if (rings.length === allRings.length) {
        if (rings.length) remaining = differenceClosed(remaining, surf.rings).filter((r) => r.length >= 3)
      } else if (rings.length) {
        remaining = differenceClosed(remaining, rings).filter((r) => r.length >= 3)
      }
    }
  }
  if (!pieces.length) return null   // nothing underneath — the attach derives to a plain wall
  // TEAR r8 (Matt: "the interior finishes need to extend down past the top
  // edge of the low roof but stop at the bottom face, covering the edge face
  // of the roof inside"): the wall's boundary is the bearing line at the
  // CORE's interior face, so its interior-finish layers sit a few inches PAST
  // the sheet's plan edge — uncovered by any floor surface, they used to fall
  // all the way to the level. A remaining sliver that hugs a floor ring's
  // edge (every vertex within reach — finish layers are inches; anything
  // running on PAST the sheet, like the gable stretch beyond the lower roof,
  // exceeds it and stays nominal) clips at that sheet's UNDERSIDE plane
  // instead, lapping the cut edge exactly.
  const EDGE_COVER_REACH = 0.6
  for (const ring of remaining) {
    let underPiece: AttachBasePiece | null = null
    if (knee) {
      // several sheets' ring edges can run along the same tear line (the upper
      // sheet's boundary is there too) — the sliver continues the sheet the
      // wall STANDS on, i.e. the lowest candidate
      const mid = ringInteriorPt(ring)
      let bestZ = Infinity
      for (const surf of knee.floors) {
        if (!surf.underAt) continue
        if (!ring.every((p) => distToRings(p, surf.rings) <= EDGE_COVER_REACH)) continue
        const z = surf.zAt(mid)
        if (z < bestZ) {
          bestZ = z
          underPiece = { ring, zAt: surf.underAt, top: 0, pieceId: nextPieceId++ }
        }
      }
    }
    pieces.push(underPiece ?? { ring, zAt: () => zBaseNominal, top: 0, pieceId: nextPieceId++ })
  }

  let prismBase = -Infinity
  for (const piece of pieces) for (const p of piece.ring) prismBase = Math.max(prismBase, piece.zAt(p))
  prismBase = Math.min(prismBase, zTop - ATTACH_MIN_HEIGHT)
  for (const piece of pieces) piece.top = prismBase
  const zOf = (piece: AttachBasePiece, p: Pt2) => Math.min(piece.zAt(p), piece.top)

  let zValley = Infinity
  for (const piece of pieces) for (const p of piece.ring) zValley = Math.min(zValley, zOf(piece, p))
  // PLAN-CUT VISIBILITY fix (2026-08-17 — same root cause as deriveSteppedBase
  // below): `prismBase` is the uniform prism's own base (the MAX across every
  // attached piece); `zValley` is the true lowest point the attached skin
  // mesh actually reaches. The caller must report zValley, never prismBase,
  // as this wall's own zBase — otherwise a 2D cut below prismBase but still
  // within the real (skinned) geometry wrongly excludes the wall.
  if (zValley >= prismBase - 1e-6) return { prismBase, trueMinZ: prismBase, mesh: null }   // flat attach — prism alone

  // WINDOW-OVER-SKIN fix (2026-08-17, extended to the attach-base case): a
  // hosted opening whose sill dips into this skin's own territory (the wedge
  // between the attached target's surface and the flat prism above) notches
  // out a below-sill remainder + an above-head resumption, mirroring
  // deriveSteppedBase's fix below. The below-sill piece reuses the SAME
  // (possibly sloped) `zAt`, just capped at the void's own sill instead of
  // the flat prism — it degrades gracefully to zero height wherever the
  // target surface is already at/above the sill (nothing to open there).
  // Skipped when the void's sill is at/above every point the target surface
  // reaches within the void's own footprint (the garage-door case: nothing
  // below the sill to preserve).
  for (const v of voids) {
    if (v.zLow >= prismBase - 1e-6) continue
    const next: AttachBasePiece[] = []
    for (const piece of pieces) {
      if (v.zLow >= piece.top - 1e-6) { next.push(piece); continue }
      const inter = intersectClosed([piece.ring], [v.rect]).filter((r) => r.length >= 3)
      if (!inter.length) { next.push(piece); continue }
      for (const r of differenceClosed([piece.ring], [v.rect]).filter((rr) => rr.length >= 3)) {
        next.push({ ring: r, zAt: piece.zAt, top: piece.top, pieceId: nextPieceId++ })
      }
      const localMin = Math.min(...inter.flatMap((r) => r.map((p) => piece.zAt(p))))
      const stackId = nextPieceId++
      if (localMin < v.zLow - 1e-6) {
        for (const r of inter) next.push({ ring: r, zAt: piece.zAt, top: v.zLow, pieceId: nextPieceId++, stackId })
      }
      if (v.zHigh < piece.top - 1e-6) {
        for (const r of inter) next.push({ ring: r, zAt: () => v.zHigh, top: piece.top, pieceId: nextPieceId++, stackId })
      }
      // else: the column opens completely up to its own ceiling — the prism
      // (or a taller neighboring piece) above carries the rest of the slot
    }
    pieces = next
  }

  const acc = new SkinAcc()

  // normalize rings CCW so the band winding matches the stem/curb base skin's convention
  const ccw = pieces.map((piece) => ({
    ...piece,
    stack: piece.stackId ?? piece.pieceId,
    ring: signedArea(piece.ring) < 0 ? [...piece.ring].reverse() : piece.ring,
  }))

  // edge census across pieces: shared creases (equal skin heights) need no step face;
  // a step between two pieces' skin heights gets a face spanning the gap.
  // WINDOW-OVER-SKIN fix (2026-08-17): entries carry their own STACK and CAP
  // (piece.top) alongside (za, zb) — grouped by stack below (see groupsOf in
  // deriveSteppedBase for the full rationale) so a void's own below-sill/
  // above-head split isn't mistaken for a genuine step between two adjacent
  // pieces, and a differing CAP between adjacent pieces (e.g. a below-sill
  // remainder capped at the sill vs. its un-notched neighbor capped at the
  // flat prism) gets its own closing face too.
  interface CensusEntry { za: number; zb: number; cap: number; stack: number }
  interface CensusRec { a: Pt2; b: Pt2; entries: CensusEntry[] }
  const census = new Map<string, CensusRec>()
  for (const piece of ccw) {
    for (let i = 0; i < piece.ring.length; i++) {
      const a = piece.ring[i], b = piece.ring[(i + 1) % piece.ring.length]
      const k = edgeKeyOf(a, b)
      const swap = keyPt(a) >= keyPt(b)
      const entry: CensusEntry = {
        za: zOf(piece, swap ? b : a), zb: zOf(piece, swap ? a : b), cap: piece.top, stack: piece.stack,
      }
      const rec = census.get(k)
      if (rec) rec.entries.push(entry)
      else census.set(k, { a: keyPt(a) < keyPt(b) ? a : b, b: keyPt(a) < keyPt(b) ? b : a, entries: [entry] })
    }
  }
  const groupsOf = (rec: CensusRec): { stack: number; za: number; zb: number; cap: number }[] => {
    const g = new Map<number, { stack: number; za: number; zb: number; cap: number }>()
    for (const e of rec.entries) {
      const cur = g.get(e.stack)
      if (cur) { cur.za = Math.min(cur.za, e.za); cur.zb = Math.min(cur.zb, e.zb); cur.cap = Math.max(cur.cap, e.cap) }
      else g.set(e.stack, { stack: e.stack, za: e.za, zb: e.zb, cap: e.cap })
    }
    return [...g.values()]
  }

  // perimeter-corner verticals, same candidate/straight-run test as deriveAttachedTop
  const cornerCand = new Map<string, { p: Pt2; dirs: Pt2[]; skinZ: number; cap: number }>()

  for (const piece of ccw) {
    const bot = piece.ring.map((p) => [p[0], p[1], zOf(piece, p)])
    const flatCoords = piece.ring.flatMap((p) => [p[0], p[1]])
    const tris = earcut(flatCoords)
    for (let i = 0; i < tris.length; i += 3) {
      const [a, b, c] = [tris[i], tris[i + 1], tris[i + 2]]
      const upOk = (piece.ring[b][0] - piece.ring[a][0]) * (piece.ring[c][1] - piece.ring[a][1])
        - (piece.ring[b][1] - piece.ring[a][1]) * (piece.ring[c][0] - piece.ring[a][0]) > 0
      // DOWN-facing skin (deriveSteppedBase's underside-cap winding, reused verbatim)
      if (upOk) acc.tri(bot[a], bot[c], bot[b])
      else acc.tri(bot[a], bot[b], bot[c])
    }
    for (let i = 0; i < piece.ring.length; i++) {
      const j = (i + 1) % piece.ring.length
      const k = edgeKeyOf(piece.ring[i], piece.ring[j])
      const rec = census.get(k)!
      if (groupsOf(rec).length < 2) {
        // perimeter band, OUTWARD winding (the 5e99257f fix) — from this piece's own
        // (varying) skin height UP to its own ceiling
        const varI = [piece.ring[i][0], piece.ring[i][1], bot[i][2]]
        const varJ = [piece.ring[j][0], piece.ring[j][1], bot[j][2]]
        const flatI = [piece.ring[i][0], piece.ring[i][1], piece.top]
        const flatJ = [piece.ring[j][0], piece.ring[j][1], piece.top]
        acc.tri(varI, flatJ, flatI)
        acc.tri(varI, varJ, flatJ)
        for (const [pi, zi] of [[i, bot[i][2]], [j, bot[j][2]]] as [number, number][]) {
          const p = piece.ring[pi]
          const key = keyPt(p)
          const q = piece.ring[pi === i ? j : i]
          const d = Math.hypot(q[0] - p[0], q[1] - p[1])
          if (d < 1e-9) continue
          const dir: Pt2 = [(q[0] - p[0]) / d, (q[1] - p[1]) / d]
          const cand = cornerCand.get(key)
          if (cand) { cand.dirs.push(dir); cand.skinZ = Math.min(cand.skinZ, zi); cand.cap = Math.max(cand.cap, piece.top) }
          else cornerCand.set(key, { p, dirs: [dir], skinZ: zi, cap: piece.top })
        }
      }
    }
  }

  // shared-edge feature lines + steps, once per unique edge — grouped by STACK
  for (const rec of census.values()) {
    const groups = groupsOf(rec)
    const za0 = Math.min(...groups.map((g) => g.za)), zb0 = Math.min(...groups.map((g) => g.zb))
    acc.edges.push(rec.a[0], rec.a[1], za0, rec.b[0], rec.b[1], zb0)
    if (groups.length === 2) {
      const [g0, g1] = groups
      // riser face for a differing BOTTOM between the two adjacent columns
      if (Math.abs(g0.za - g1.za) > 1e-6 || Math.abs(g0.zb - g1.zb) > 1e-6) {
        const A0 = [rec.a[0], rec.a[1], g0.za], B0 = [rec.b[0], rec.b[1], g0.zb]
        const A1 = [rec.a[0], rec.a[1], g1.za], B1 = [rec.b[0], rec.b[1], g1.zb]
        acc.tri(A0, B0, B1); acc.tri(A0, B1, A1)
        acc.tri(A0, B1, B0); acc.tri(A0, A1, B1)
        acc.edges.push(rec.a[0], rec.a[1], g1.za, rec.b[0], rec.b[1], g1.zb)
      }
      // WINDOW-OVER-SKIN fix (2026-08-17): a differing CEILING between two
      // adjacent columns (e.g. a below-sill remainder capped at the void's
      // sill vs. its un-notched neighbor capped at the flat prism) needs its
      // own closing face too — previously every piece shared the same
      // implicit ceiling (`prismBase`), so this case never arose.
      if (Math.abs(g0.cap - g1.cap) > 1e-6) {
        const capLo = Math.min(g0.cap, g1.cap), capHi = Math.max(g0.cap, g1.cap)
        const A0 = [rec.a[0], rec.a[1], capLo], B0 = [rec.b[0], rec.b[1], capLo]
        const A1 = [rec.a[0], rec.a[1], capHi], B1 = [rec.b[0], rec.b[1], capHi]
        acc.tri(A0, B0, B1); acc.tri(A0, B1, A1)
        acc.tri(A0, B1, B0); acc.tri(A0, A1, B1)
        acc.edges.push(rec.a[0], rec.a[1], capHi, rec.b[0], rec.b[1], capHi)
      }
    }
  }

  // emit the perimeter-corner verticals: a vertex is a real silhouette corner when
  // its perimeter edges don't continue straight through it
  for (const cand of cornerCand.values()) {
    if (cand.dirs.length < 2) continue
    let straight = true
    for (let i = 1; i < cand.dirs.length && straight; i++) {
      const cross = Math.abs(cand.dirs[0][0] * cand.dirs[i][1] - cand.dirs[0][1] * cand.dirs[i][0])
      if (cross > 0.01) straight = false
    }
    if (straight) continue
    if (cand.cap - cand.skinZ < 1e-6) continue
    acc.edges.push(cand.p[0], cand.p[1], cand.cap, cand.p[0], cand.p[1], cand.skinZ)
  }

  return { prismBase, trueMinZ: zValley, mesh: acc.empty ? null : acc.build() }
}

// ---------- stepped base (CURB + WALL-IS-STEM rounds, 2026-08-10) ----------
// The wall body's bottom derives from the foundation layout the same way the
// attached top does: the prism starts at the HIGHEST per-run bottom and a mesh base
// fills from there down to each run's own bottom. Two callers: the CURB (stem tops
// stepped UP past the wall base — framing never starts below grade) and WALL-IS-STEM
// (the wall extends DOWN to the footing tops, replacing the stem). Openings are NOT
// cut from the base skin (v1 — an opening into a curb region or a below-grade
// extension is a design conflict to resolve, not geometry to honor; the deferred
// sill pieces fill the prism slots down to the final base).

function deriveSteppedBase(
  bodyOutlines: Pt2[][],
  zBase: number,
  zTopPrism: number,
  layout: import('./footing').FoundationLayout,
  bottoms: { s0: number; s1: number; z: number }[],
  wallThicknessFt: number,
  voids: { rect: Pt2[]; zLow: number; zHigh: number }[] = [],
): { prismBase: number; trueMinZ: number; mesh: NonNullable<ElementSolid['mesh']> | null } | null {
  interface BasePiece {
    ring: Pt2[]
    z: number
    /**
     * WINDOW-OVER-SKIN fix (2026-08-17): this piece's own ceiling, when lower
     * than the flat prism base — set on the below-sill remainder of a void
     * notch (see the punch loop below). Absent = `prismBase` (the ordinary,
     * unnotched case).
     */
    capZ?: number
    /** unique per piece; identifies it for shared-edge grouping below */
    pieceId: number
    /**
     * Set ONLY on the below-sill/above-head siblings a void notch splits a
     * single piece into — shared between them so the census below recognizes
     * they're the SAME vertically-stacked column (no connecting face between
     * them; that gap IS the opening) rather than two genuinely adjacent,
     * side-by-side pieces (which DO need a connecting/riser face). Absent =
     * this piece's own identity (`pieceId`), i.e. it stands alone.
     */
    stackId?: number
  }
  let nextPieceId = 0
  const zCap = zTopPrism - ATTACH_MIN_HEIGHT
  const halfW = wallThicknessFt + layout.stemW + 1   // generous — clipped to the body anyway
  let remaining = bodyOutlines
  let pieces: BasePiece[] = []
  for (const run of bottoms) {
    if (!remaining.length) break
    if (Math.abs(run.z - zBase) <= 1e-6) continue   // at the nominal base — stays 'remaining'
    const pts = foundationRunPts(layout, run.s0, run.s1)
    if (pts.length < 2) continue
    const band = inflateOpen(pts, halfW)
    if (!band.length) continue
    const rings = intersectClosed(remaining, band).filter((r) => r.length >= 3)
    for (const ring of rings) pieces.push({ ring, z: Math.min(run.z, zCap), pieceId: nextPieceId++ })
    if (rings.length) remaining = differenceClosed(remaining, band).filter((r) => r.length >= 3)
  }
  if (!pieces.length) return null
  for (const ring of remaining) pieces.push({ ring, z: Math.min(zBase, zCap), pieceId: nextPieceId++ })

  let prismBase = -Infinity
  for (const piece of pieces) prismBase = Math.max(prismBase, piece.z)
  if (!(prismBase < zCap) || !Number.isFinite(prismBase)) return null
  if (pieces.every((p) => Math.abs(p.z - prismBase) <= 1e-6) && Math.abs(prismBase - zBase) <= 1e-6) return null

  // OPENINGS CUT THE CURB (2026-08-10, Matt's garage-door report; extended
  // 2026-08-17 — Matt's Forsyth report, WINDOW-OVER-SKIN fix): a void notches
  // whichever part of a piece's own column it actually reaches. A door at floor
  // level (`v.zLow` at/below the piece's own bottom) opens the column fully, or
  // down to a hanging piece at the void head. A WINDOW's sill commonly floats
  // ABOVE the piece's own bottom instead (the ordinary case on a locally-low-
  // terrain stretch of a curbed/stepped wall) — the original round only handled
  // the door case and left a floating-sill column entirely uncut, which is what
  // left the window unopened here. Real material below a floating sill still
  // has to survive, so that remainder is kept as its own piece capped at the
  // void's sill instead of skipping the notch altogether.
  for (const v of voids) {
    if (v.zLow >= prismBase - 1e-6) continue
    const next: BasePiece[] = []
    for (const piece of pieces) {
      const cap = piece.capZ ?? prismBase
      if (v.zLow >= cap - 1e-6 || piece.z >= cap - 1e-6) { next.push(piece); continue }
      const inter = intersectClosed([piece.ring], [v.rect]).filter((r) => r.length >= 3)
      if (!inter.length) { next.push(piece); continue }
      for (const r of differenceClosed([piece.ring], [v.rect]).filter((rr) => rr.length >= 3)) {
        next.push({ ring: r, z: piece.z, capZ: cap, pieceId: nextPieceId++ })
      }
      // below-sill and above-head are the SAME vertically-stacked column, split
      // by the void — tag them with a shared stackId so the census below draws
      // NO face between them (that gap IS the window) while each still draws
      // its own perimeter band independently
      const stackId = nextPieceId++
      if (v.zLow > piece.z + 1e-6) {
        // below-sill remainder: real material from this piece's own bottom up
        // to the void's sill, capped there instead of at the piece's own ceiling
        for (const r of inter) next.push({ ring: r, z: piece.z, capZ: v.zLow, pieceId: nextPieceId++, stackId })
      }
      if (v.zHigh < cap - 1e-6) {
        for (const r of inter) next.push({ ring: r, z: v.zHigh, capZ: cap, pieceId: nextPieceId++, stackId })
      }
      // else: the column opens completely up to its own ceiling — the prism
      // (or a taller neighboring piece) above carries the rest of the slot
    }
    pieces = next
  }
  if (!pieces.length) return null

  const acc = new SkinAcc()
  const ccw = pieces.map((piece) => ({
    z: piece.z,
    // WINDOW-OVER-SKIN fix (2026-08-17): each piece's own ceiling — the flat
    // prism base, or a void's own sill for a below-sill remainder.
    cap: piece.capZ ?? prismBase,
    // identifies which vertically-stacked column this piece belongs to — see
    // BasePiece.stackId
    stack: piece.stackId ?? piece.pieceId,
    ring: signedArea(piece.ring) < 0 ? [...piece.ring].reverse() : piece.ring,
  }))

  interface CensusRec { a: Pt2; b: Pt2; entries: { z: number; cap: number; stack: number }[] }
  const census = new Map<string, CensusRec>()
  for (const piece of ccw) {
    for (let i = 0; i < piece.ring.length; i++) {
      const a = piece.ring[i], b = piece.ring[(i + 1) % piece.ring.length]
      const k = edgeKeyOf(a, b)
      const rec = census.get(k)
      const entry = { z: piece.z, cap: piece.cap, stack: piece.stack }
      if (rec) rec.entries.push(entry)
      else census.set(k, { a: keyPt(a) < keyPt(b) ? a : b, b: keyPt(a) < keyPt(b) ? b : a, entries: [entry] })
    }
  }
  // WINDOW-OVER-SKIN fix (2026-08-17): group an edge's contributing entries by
  // STACK, not by raw piece — the below-sill/above-head siblings of a single
  // void notch are the SAME column (2 raw entries, 1 group: no connecting face
  // between them, that gap IS the opening) vs. two genuinely adjacent,
  // side-by-side pieces (2 raw entries, 2 DIFFERENT groups: a real riser/step
  // face is needed). Each group collapses to [zMin, capMax] across its own
  // entries — "what this piece's column would look like to its neighbor,"
  // ignoring any internal void gap.
  const groupsOf = (rec: CensusRec): { stack: number; z: number; cap: number }[] => {
    const g = new Map<number, { stack: number; z: number; cap: number }>()
    for (const e of rec.entries) {
      const cur = g.get(e.stack)
      if (cur) { cur.z = Math.min(cur.z, e.z); cur.cap = Math.max(cur.cap, e.cap) }
      else g.set(e.stack, { stack: e.stack, z: e.z, cap: e.cap })
    }
    return [...g.values()]
  }
  const cornerCand = new Map<string, { p: Pt2; dirs: Pt2[]; z: number; cap: number }>()

  for (const piece of ccw) {
    // skip pieces already at their own ceiling — the prism's own bottom cap
    // (or a resuming piece's own cap) covers them
    const skinned = piece.z < piece.cap - 1e-6
    if (skinned) {
      const flatCoords = piece.ring.flatMap((p) => [p[0], p[1]])
      const tris = earcut(flatCoords)
      for (let i = 0; i < tris.length; i += 3) {
        const [a, b, c] = [tris[i], tris[i + 1], tris[i + 2]]
        const A = [piece.ring[a][0], piece.ring[a][1], piece.z]
        const B = [piece.ring[b][0], piece.ring[b][1], piece.z]
        const C = [piece.ring[c][0], piece.ring[c][1], piece.z]
        const upOk = (piece.ring[b][0] - piece.ring[a][0]) * (piece.ring[c][1] - piece.ring[a][1])
          - (piece.ring[b][1] - piece.ring[a][1]) * (piece.ring[c][0] - piece.ring[a][0]) > 0
        // DOWN-facing underside skin
        if (upOk) acc.tri(A, C, B)
        else acc.tri(A, B, C)
      }
    }
    for (let i = 0; i < piece.ring.length; i++) {
      const j = (i + 1) % piece.ring.length
      const k = edgeKeyOf(piece.ring[i], piece.ring[j])
      const rec = census.get(k)!
      if (groupsOf(rec).length < 2 && skinned) {
        // perimeter band from this piece's base UP TO ITS OWN CEILING — OUTWARD
        // winding (for a CCW ring the outside is RIGHT of travel; the first cut of
        // this faced inward and backface-culled, reading as a transparent wall face
        // — Matt's Forsyth report, 2026-08-10)
        const botI = [piece.ring[i][0], piece.ring[i][1], piece.z]
        const botJ = [piece.ring[j][0], piece.ring[j][1], piece.z]
        const topI = [piece.ring[i][0], piece.ring[i][1], piece.cap]
        const topJ = [piece.ring[j][0], piece.ring[j][1], piece.cap]
        acc.tri(botI, topJ, topI)
        acc.tri(botI, botJ, topJ)
        for (const pi of [i, j]) {
          const p = piece.ring[pi]
          const q = piece.ring[pi === i ? j : i]
          const d = Math.hypot(q[0] - p[0], q[1] - p[1])
          if (d < 1e-9) continue
          const dir: Pt2 = [(q[0] - p[0]) / d, (q[1] - p[1]) / d]
          const key = keyPt(p)
          const cand = cornerCand.get(key)
          if (cand) { cand.dirs.push(dir); cand.z = Math.min(cand.z, piece.z); cand.cap = Math.max(cand.cap, piece.cap) }
          else cornerCand.set(key, { p, dirs: [dir], z: piece.z, cap: piece.cap })
        }
      }
    }
  }

  // step faces + underside outlines, once per unique edge — grouped by STACK
  // (see groupsOf above), never raw entry, so a void's own internal gap is
  // never mistaken for a riser between two adjacent columns
  for (const rec of census.values()) {
    const groups = groupsOf(rec)
    const zLo = Math.min(...groups.map((g) => g.z))
    const capHi = Math.max(...groups.map((g) => g.cap))
    if (zLo < capHi - 1e-6) {
      acc.edges.push(rec.a[0], rec.a[1], zLo, rec.b[0], rec.b[1], zLo)
    }
    if (groups.length === 2) {
      const [g0, g1] = groups
      // riser face for a differing BOTTOM between the two adjacent columns
      if (Math.abs(g0.z - g1.z) > 1e-6) {
        const A0 = [rec.a[0], rec.a[1], g0.z], B0 = [rec.b[0], rec.b[1], g0.z]
        const A1 = [rec.a[0], rec.a[1], g1.z], B1 = [rec.b[0], rec.b[1], g1.z]
        acc.tri(A0, B0, B1); acc.tri(A0, B1, A1)
        acc.tri(A0, B1, B0); acc.tri(A0, A1, B1)
        acc.edges.push(rec.a[0], rec.a[1], Math.max(g0.z, g1.z), rec.b[0], rec.b[1], Math.max(g0.z, g1.z))
      }
      // WINDOW-OVER-SKIN fix (2026-08-17): a differing CEILING between two
      // adjacent columns is now possible too (e.g. a void's below-sill column
      // capped at the sill vs. its un-notched plan neighbor capped at the
      // flat prism base) — close that delta with the same kind of face
      if (Math.abs(g0.cap - g1.cap) > 1e-6) {
        const capLo = Math.min(g0.cap, g1.cap), capHiPair = Math.max(g0.cap, g1.cap)
        const A0 = [rec.a[0], rec.a[1], capLo], B0 = [rec.b[0], rec.b[1], capLo]
        const A1 = [rec.a[0], rec.a[1], capHiPair], B1 = [rec.b[0], rec.b[1], capHiPair]
        acc.tri(A0, B0, B1); acc.tri(A0, B1, A1)
        acc.tri(A0, B1, B0); acc.tri(A0, A1, B1)
        acc.edges.push(rec.a[0], rec.a[1], capHiPair, rec.b[0], rec.b[1], capHiPair)
      }
    }
  }
  for (const cand of cornerCand.values()) {
    if (cand.dirs.length < 2) continue
    let straight = true
    for (let i = 1; i < cand.dirs.length && straight; i++) {
      const cross = Math.abs(cand.dirs[0][0] * cand.dirs[i][1] - cand.dirs[0][1] * cand.dirs[i][0])
      if (cross > 0.01) straight = false
    }
    if (straight) continue
    if (cand.cap - cand.z < 1e-6) continue
    acc.edges.push(cand.p[0], cand.p[1], cand.z, cand.p[0], cand.p[1], cand.cap)
  }

  // PLAN-CUT VISIBILITY fix (2026-08-17, Matt's report: a footed wall on a
  // stepped/terrain site vanished from plan views wherever the cut height fell
  // below its HIGHEST step): `prismBase` is the uniform prism's own base — by
  // construction the MAX across every stepped piece — which is correct for
  // building the prism/sill/framing, but WRONG as "how far down this wall's
  // geometry actually reaches." The skinned pieces below `prismBase` are real,
  // rendered geometry (the stepped skin mesh) that a 2D plan/section cut must
  // still find. `trueMinZ` is the true bottom of everything this call built —
  // the caller reports THIS as the element's own zBase, never `prismBase`.
  let trueMinZ = prismBase
  for (const piece of pieces) trueMinZ = Math.min(trueMinZ, piece.z)
  return { prismBase, trueMinZ, mesh: acc.empty ? null : acc.build() }
}

// ---------- FINISH SKIRT (2026-08-16, Matt's exposed-band report) ----------
// "Run the sheathing and finish across the band joist — that should be the
// default condition." An exterior wall's outside-of-core layers stop at the
// body's derived base, exposing the floor band (and the mudsill) between
// storeys. The skirt extends them DOWN:
// - DEFAULT: to the top of concrete — footed walls to the stem top per run
//   (covering the band + mudsill on bearing stretches; a crawl wall's body
//   already reaches its stepped stem, so the default skirt is empty there);
//   non-footed walls standing on a wood floor's sheathing, down over the band
//   to the joist underside.
// - `finishToGrade` (Matt's option 2): the finish (sheathing riding along as
//   furring) continues over the exposed concrete to a 4" clearance ABOVE the
//   grade line, the bottom edge RUNNING AT AN ANGLE with the sampled grade —
//   the weep-screed read, never quantized to the step grid. Crawl-space walls
//   respect it too: the skirt clads the exposed stem below the crawl framing.
// One meshOnly `skirt` solid per wall (see the ElementSolid flag note). v1
// scope: straight open walls (loops/curves skipped — the framing precedent);
// butt ends at corners (adjacent walls' skirts meet like unwelded bodies —
// recorded backlog alongside the corner-weld skins).
/**
 * SKIRT CORNER MITER (2026-10-04, Matt: "the new thicker paint applied materials don't
 * properly clean up at the corner joins in the skirt area that extends below the
 * framing"). The other wall meeting this one's start/end — the join ref first, else a
 * same-level exterior wall whose own end lies at this end — with its skirt's outer and
 * inner face lines, so the two skirts can meet on the miter instead of butting square
 * (which left an open notch at every outside corner and a capped seam at inside ones).
 */
function skirtPartnerLines(
  el: ResolvedElement, end: 'start' | 'end', myEnd: Pt2, byId: Map<string, ResolvedElement>,
): { outer: [Pt2, Pt2]; inner: [Pt2, Pt2] } | null {
  const ref = el.params[end === 'start' ? 'startJoin' : 'endJoin'] as { targetId?: string; targetEnd?: 'start' | 'end' } | null | undefined
  let partner: ResolvedElement | null = null
  let pEnd: 'start' | 'end' | null = null
  if (ref?.targetId) {
    const p = byId.get(ref.targetId)
    if (p && p.category === 'wall') { partner = p; pEnd = ref.targetEnd === 'end' ? 'end' : 'start' }
  }
  if (!partner) {
    byId.forEach((p) => {
      if (partner || p.id === el.id || p.category !== 'wall' || p.levelId !== el.levelId) return
      const pp = p.params.path as Path2D | null
      if (!pp?.nodes || pp.nodes.length < 2 || pp.closed) return
      const a = pp.nodes[0].pt, b = pp.nodes[pp.nodes.length - 1].pt
      if (Math.hypot(a[0] - myEnd[0], a[1] - myEnd[1]) < 0.05) { partner = p; pEnd = 'start' }
      else if (Math.hypot(b[0] - myEnd[0], b[1] - myEnd[1]) < 0.05) { partner = p; pEnd = 'end' }
    })
  }
  const P = partner as ResolvedElement | null
  if (!P || !pEnd || P.type.params.function !== 'exterior' || P.params.noFinishSkirt === true) return null
  const pPath = P.params.path as Path2D | null
  if (!pPath?.nodes || pPath.nodes.length < 2 || pPath.closed) return null
  if (pPath.nodes.some((n) => Math.abs(n.bulge ?? 0) > 1e-12)) return null
  const pZones = assemblyZonesFt(P)
  if (pZones.aboveFt < 1 / 48) return null
  const pDelta = centerlineOffsetFt(P.params.locationLine, P.params.flipped, P.assembly)
  const pSign = P.params.flipped === true ? -1 : 1
  const pT = P.assemblyThicknessFt
  const line = (off: number): [Pt2, Pt2] | null => {
    const o = offsetPolyline(pPath, off)
    if (!o) return null
    const f = flattenPath(o)
    if (f.length < 2) return null
    return pEnd === 'start' ? [f[0], f[1]] : [f[f.length - 1], f[f.length - 2]]
  }
  const pBand = P.faceBandSteps?.exterior?.[0]?.deltaFt ?? 0
  const outer = line(pDelta + pSign * (pT / 2 + pBand))
  const inner = line(pDelta + pSign * (pT / 2 - pZones.aboveFt))
  return outer && inner ? { outer, inner } : null
}

/** where line a0→a1 (extended) crosses line b0→b1 (extended); null when ~parallel */
function lineCross(a0: Pt2, a1: Pt2, b0: Pt2, b1: Pt2): Pt2 | null {
  const dx = a1[0] - a0[0], dy = a1[1] - a0[1], ex = b1[0] - b0[0], ey = b1[1] - b0[1]
  const den = dx * ey - dy * ex
  const l = Math.hypot(dx, dy) * Math.hypot(ex, ey)
  if (l < 1e-12 || Math.abs(den) / l < 0.05) return null
  const u = ((b0[0] - a0[0]) * ey - (b0[1] - a0[1]) * ex) / den
  return [a0[0] + dx * u, a0[1] + dy * u]
}

const SKIRT_GRADE_CLEARANCE_FT = 4 / 12
const SKIRT_SAMPLE_FT = 2

function buildFinishSkirt(
  el: ResolvedElement,
  workPath: Path2D,
  refLine: Pt2[],
  frame: ReturnType<typeof wallFrame>,
  delta: number,
  t: number,
  zBase: number,
  isLoop: boolean,
  byId?: Map<string, ResolvedElement>,
  openings: { s0: number; s1: number; zLow: number }[] = [],
): ElementSolid | null {
  if (isLoop || workPath.closed || !byId) return null
  if (el.type.params.function !== 'exterior') return null
  if (workPath.nodes.some((n) => Math.abs(n.bulge ?? 0) > 1e-12)) return null
  const zones = assemblyZonesFt(el)
  const layers = el.assembly?.layers
  if (!layers?.length || zones.aboveFt < 1 / 48) return null
  if (Array.isArray(el.params.baseAttachIds) && (el.params.baseAttachIds as unknown[]).length) return null
  if (el.params.noFinishSkirt === true) return null

  const sign: 1 | -1 = el.params.flipped === true ? -1 : 1
  // THE BOTTOM BAND CONTINUES DOWN (2026-10-04, Matt: "we want the face finish to behave
  // as we'd expect no matter how the face is generated"): the skirt wears the exterior
  // face's LOWEST band — a stone wainscot runs on down as stone, at the stone's own face
  // (its band step + the body's band recess) — not the face's top finish
  const bottomBand = faceBands(el.params, 'exterior')[0]
  const bottomStep = el.faceBandSteps?.exterior?.[0]
  const bandOut = bottomStep ? bottomStep.deltaFt + recessBySide(el).exterior : 0
  const skirtMat = bottomBand?.materialId || layers[0].materialId
  const offOuter = delta + sign * (t / 2 + bandOut)
  const offInner = delta + sign * (t / 2 - zones.aboveFt)
  const outerPath = offsetPolyline(workPath, offOuter)
  const innerPath = offsetPolyline(workPath, offInner)
  if (!outerPath || !innerPath) return null
  const outer = flattenPath(outerPath)
  const inner = flattenPath(innerPath)
  if (outer.length !== refLine.length || inner.length !== refLine.length) return null
  const L = frame.totalLength
  if (L < 1e-4) return null
  // SKIRT CORNER MITER: each end that meets another skirted exterior wall runs to the
  // miter with that wall's skirt (outer face to outer face, inner to inner) — no cap
  // there, the partner's skirt closes the corner
  const mitered = { start: false, end: false }
  for (const end of ['start', 'end'] as const) {
    const pl = skirtPartnerLines(el, end, end === 'start' ? refLine[0] : refLine[refLine.length - 1], byId)
    if (!pl) continue
    const n = outer.length
    const [i0, i1] = end === 'start' ? [0, 1] : [n - 1, n - 2]
    const o = lineCross(outer[i1], outer[i0], pl.outer[0], pl.outer[1])
    const q = lineCross(inner[i1], inner[i0], pl.inner[0], pl.inner[1])
    // never further than a couple of finish thicknesses from where the end was
    if (!o || !q || Math.hypot(o[0] - outer[i0][0], o[1] - outer[i0][1]) > 1.5 || Math.hypot(q[0] - inner[i0][0], q[1] - inner[i0][1]) > 1.5) continue
    outer[i0] = o
    inner[i0] = q
    mitered[end] = true
  }

  // refLine station → the matching (segment-parameter) point on an offset polyline
  // (moved ahead of the vertical-profile block below — the non-footed branch needs
  // it to find EXACT floor-band coverage crossings, not just sample at the grid)
  const cum: number[] = [0]
  for (let i = 0; i + 1 < refLine.length; i++) {
    cum.push(cum[i] + Math.hypot(refLine[i + 1][0] - refLine[i][0], refLine[i + 1][1] - refLine[i][1]))
  }

  // ---- vertical profile: skirt TOP = the body's derived bottom at the station,
  // skirt BOTTOM = top of concrete (default) or the grade line + clearance
  let topAt: (s: number) => number
  let botAt: (s: number) => number
  const breaks: number[] = []
  if (el.params.footingEnabled) {
    const layout = foundationLayoutFor(el, byId)
    if (!layout || layout.closed || !layout.runs.length) return null
    const finishToGrade = el.params.finishToGrade === true && !el.params.wallIsStem
    const runFor = (s: number) =>
      layout.runs.find((r) => s >= r.s0 - 1e-6 && s <= r.s1 + 1e-6) ?? layout.runs[layout.runs.length - 1]
    topAt = (s) => { const r = runFor(s); return r.floorTop ?? r.stemTop }
    // TERRAIN DECOUPLE round (docs/bim-plan/19): a captured wall's skirt grade
    // line reads the fit's own skirt lane (sampled at this exact offset at
    // capture time) — never the live TIN, so a captured wall's whole generate
    // is terrain-free. No capture = the live point sample (pre-round behavior).
    const fit = terrainFitOf(el.params.terrainFit)
    botAt = (s) => {
      const r = runFor(s)
      if (!finishToGrade) return r.stemTop
      const sc = Math.max(0, Math.min(s, L))
      let g: number | null
      if (fit) {
        g = terrainFitLane(fit.skirtStations, fit.skirt, sc / L)
      } else {
        const at = frame.pointAt(sc)
        const n: Pt2 = [-at.dir[1], at.dir[0]]
        const off = offOuter + sign * 0.25
        g = foundationGradeAt(byId, [at.pt[0] + n[0] * off, at.pt[1] + n[1] * off])
      }
      if (g === null) return r.stemTop
      return Math.max(r.footingTop, Math.min(r.stemTop, g + SKIRT_GRADE_CLEARANCE_FT))
    }
    for (const r of layout.runs) breaks.push(r.s0, r.s1)
  } else {
    // platform wall standing on a wood floor's sheathing — skirt over the band
    let found: FloorPlatformBand | null = null
    byId.forEach((cand) => {
      if (found || cand.id === el.id) return
      const b = floorPlatformBand(cand)
      if (b && Math.abs(b.zSheathTop - zBase) < 0.3) found = b
    })
    if (!found) return null
    const band: FloorPlatformBand = found
    const coveredAt = (s: number): boolean => {
      const at = frame.pointAt(Math.max(0, Math.min(s, L)))
      const n: Pt2 = [-at.dir[1], at.dir[0]]
      const p: Pt2 = [at.pt[0] + n[0] * delta, at.pt[1] + n[1] * delta]
      return band.rings.some((r) => pointInRingW(p, r))
    }
    topAt = () => zBase
    botAt = (s) => (coveredAt(s) ? band.zJoistBot : zBase)
    // exact profile stations: crossings of coveredAt()'s own test line (refLine
    // offset by `delta`) against the band's rings — a jog in the floor's own
    // boundary otherwise falls mid-cell between the fixed-grid samples below and
    // gets straight-line-interpolated into a diagonal face instead of resolving
    // as a clean vertical step at the true coverage transition (per-segment,
    // mirrors wall/framing.ts's own crossingStations)
    for (let i = 0; i + 1 < refLine.length; i++) {
      const segLen = cum[i + 1] - cum[i]
      if (segLen < 1e-9) continue
      const dir: Pt2 = [(refLine[i + 1][0] - refLine[i][0]) / segLen, (refLine[i + 1][1] - refLine[i][1]) / segLen]
      const n: Pt2 = [-dir[1], dir[0]]
      const P0: Pt2 = [refLine[i][0] + n[0] * delta, refLine[i][1] + n[1] * delta]
      for (const ring of band.rings) {
        for (let j = 0; j < ring.length; j++) {
          const A = ring[j], B = ring[(j + 1) % ring.length]
          const E: Pt2 = [B[0] - A[0], B[1] - A[1]]
          const den = dir[0] * E[1] - dir[1] * E[0]
          if (Math.abs(den) < 1e-12) continue
          const dx = A[0] - P0[0], dy = A[1] - P0[1]
          const tSeg = (dx * E[1] - dy * E[0]) / den
          const tEdge = (dx * dir[1] - dy * dir[0]) / den
          if (tSeg > 1e-6 && tSeg < segLen - 1e-6 && tEdge >= -1e-6 && tEdge <= 1 + 1e-6) {
            breaks.push(cum[i] + tSeg)
          }
        }
      }
    }
  }

  // ---- stations: path vertices + profile breaks + a bounded regular sampling
  // (grade slope and band-coverage transitions resolve at sample resolution)
  const stations: number[] = [0, L]
  {
    let acc = 0
    for (let i = 0; i + 1 < refLine.length; i++) {
      acc += Math.hypot(refLine[i + 1][0] - refLine[i][0], refLine[i + 1][1] - refLine[i][1])
      if (acc > 1e-6 && acc < L - 1e-6) stations.push(acc)
    }
  }
  for (const b of breaks) if (b > 1e-6 && b < L - 1e-6) stations.push(b)
  // DOORS DOWN TO THE SKIRT (2026-10-07, Matt: Santiago's garage door had brick running
  // across its foot): an opening whose bottom comes down into the skirt cuts it there — the
  // skirt stops square at each jamb (the caps below close it) and keeps only what lies below
  // the opening's bottom
  // the skirt's stations run along its OUTER line by segment fraction (a mitered end stretches
  // it), so a jamb is found where the wall's own station projects onto that line
  const toSkirtS = (s0: number): number => {
    let i = 0
    while (i + 2 < cum.length && s0 > cum[i + 1] + 1e-9) i++
    const a = refLine[i], b = refLine[i + 1], oa = outer[i], ob = outer[i + 1]
    const sl = cum[i + 1] - cum[i]
    if (sl < 1e-9) return s0
    const P: Pt2 = [a[0] + (b[0] - a[0]) * (s0 - cum[i]) / sl, a[1] + (b[1] - a[1]) * (s0 - cum[i]) / sl]
    const dx = ob[0] - oa[0], dy = ob[1] - oa[1], ol = Math.hypot(dx, dy)
    if (ol < 1e-9) return s0
    const uo = ((P[0] - oa[0]) * dx + (P[1] - oa[1]) * dy) / (ol * ol)
    return cum[i] + uo * sl
  }
  const cuts = openings.map((o) => ({ a: toSkirtS(o.s0), b: toSkirtS(o.s1), zLow: o.zLow }))
  for (const o of cuts) for (const b of [o.a, o.b]) if (b > 1e-6 && b < L - 1e-6) stations.push(b)
  const openTop = (s: number, top: number) => {
    let t = top
    for (const o of cuts) if (s > Math.min(o.a, o.b) + 1e-6 && s < Math.max(o.a, o.b) - 1e-6) t = Math.min(t, o.zLow)
    return t
  }
  const nS = Math.min(512, Math.max(1, Math.ceil(L / SKIRT_SAMPLE_FT)))
  for (let i = 1; i < nS; i++) stations.push((L * i) / nS)
  stations.sort((a, b) => a - b)

  const ptOn = (line: Pt2[], s: number): Pt2 => {
    let i = 0
    while (i + 2 < cum.length && s > cum[i + 1] + 1e-9) i++
    const segLen = cum[i + 1] - cum[i]
    const u = segLen > 1e-9 ? Math.max(0, Math.min(1, (s - cum[i]) / segLen)) : 0
    return [line[i][0] + (line[i + 1][0] - line[i][0]) * u, line[i][1] + (line[i + 1][1] - line[i][1]) * u]
  }

  interface Cell { sa: number; sb: number; top: number; botA: number; botB: number; active: boolean }
  const cells: Cell[] = []
  for (let i = 0; i + 1 < stations.length; i++) {
    const sa = stations[i], sb = stations[i + 1]
    if (sb - sa < 1e-4) continue
    const top = openTop((sa + sb) / 2, topAt((sa + sb) / 2))
    const botA = Math.min(top, botAt(sa + (sb - sa) * 1e-3))
    const botB = Math.min(top, botAt(sb - (sb - sa) * 1e-3))
    cells.push({ sa, sb, top, botA, botB, active: top - Math.min(botA, botB) > 1e-3 })
  }
  if (!cells.some((c) => c.active)) return null

  const acc = new SkinAcc()
  // the outer face's triangles: the FINISH FACE the surface-detail pass dresses
  const ff: number[] = []
  const V = (p: Pt2, z: number): number[] => [p[0], p[1], z]
  const quad = (a: number[], b: number[], c: number[], d: number[], want: [number, number, number]) => {
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2]
    const vx = d[0] - a[0], vy = d[1] - a[1], vz = d[2] - a[2]
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
    if (nx * want[0] + ny * want[1] + nz * want[2] >= 0) { acc.tri(a, b, c); acc.tri(a, c, d) }
    else { acc.tri(a, c, b); acc.tri(a, d, c) }
  }
  /** transverse cap between the outer/inner faces at station s over [z0, z1],
   *  facing ±dir — a skirt start/stop, or the exposed part of a profile step */
  const capAt = (s: number, z0: number, z1: number, facing: 1 | -1) => {
    if (z1 - z0 < 1e-3) return
    const o = ptOn(outer, s), q = ptOn(inner, s)
    const at = frame.pointAt(Math.max(0, Math.min(s, L)))
    const want: [number, number, number] = [facing * at.dir[0], facing * at.dir[1], 0]
    quad(V(o, z0), V(q, z0), V(q, z1), V(o, z1), want)
    acc.edges.push(o[0], o[1], z0, o[0], o[1], z1)
  }
  let zMin = Infinity, zMax = -Infinity
  // the cells at the wall's own two ends (a mitered end gets no cap there)
  const firstActive = cells.findIndex((c) => c.active && c.sa < 1e-6)
  let lastActive = -1
  for (let i = cells.length - 1; i >= 0; i--) if (cells[i].active && cells[i].sb > L - 1e-6) { lastActive = i; break }
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i]
    if (!c.active) continue
    zMin = Math.min(zMin, c.botA, c.botB)
    zMax = Math.max(zMax, c.top)
    const oA = ptOn(outer, c.sa), oB = ptOn(outer, c.sb)
    const iA = ptOn(inner, c.sa), iB = ptOn(inner, c.sb)
    const out2: [number, number, number] = [oA[0] - iA[0], oA[1] - iA[1], 0]
    quad(V(oA, c.botA), V(oB, c.botB), V(oB, c.top), V(oA, c.top), out2)
    ff.push(...V(oA, c.botA), ...V(oB, c.botB), ...V(oB, c.top), ...V(oA, c.botA), ...V(oB, c.top), ...V(oA, c.top))
    quad(V(iA, c.botA), V(iB, c.botB), V(iB, c.top), V(iA, c.top), [-out2[0], -out2[1], 0])
    quad(V(oA, c.botA), V(oB, c.botB), V(iB, c.botB), V(iA, c.botA), [0, 0, -1])
    // the screed line — the skirt's bottom edge is elevation-critical linework
    acc.edges.push(oA[0], oA[1], c.botA, oB[0], oB[1], c.botB)
    // caps: only the sub-intervals the neighbor does NOT continue (coincident
    // coplanar overlap would z-fight; a matching neighbor face needs no cap)
    const prev = i > 0 && Math.abs(cells[i - 1].sb - c.sa) < 1e-6 && cells[i - 1].active ? cells[i - 1] : null
    const next = i + 1 < cells.length && Math.abs(cells[i + 1].sa - c.sb) < 1e-6 && cells[i + 1].active ? cells[i + 1] : null
    if (!prev) {
      if (!(mitered.start && i === firstActive)) capAt(c.sa, c.botA, c.top, -1)
      else acc.edges.push(oA[0], oA[1], c.botA, oA[0], oA[1], c.top)   // the corner itself
    }
    else {
      if (c.top > prev.top + 1e-3) capAt(c.sa, Math.max(c.botA, prev.top), c.top, -1)
      if (c.botA < prev.botB - 1e-3) capAt(c.sa, c.botA, Math.min(prev.botB, c.top), -1)
    }
    if (!next) {
      if (!(mitered.end && i === lastActive)) capAt(c.sb, c.botB, c.top, 1)
      else acc.edges.push(oB[0], oB[1], c.botB, oB[0], oB[1], c.top)   // the corner itself
    }
    else {
      if (c.top > next.top + 1e-3) capAt(c.sb, Math.max(c.botB, next.top), c.top, 1)
      if (c.botB < next.botA - 1e-3) capAt(c.sb, c.botB, Math.min(next.botA, c.top), 1)
    }
  }
  if (acc.empty) return null
  return {
    elementId: el.id,
    category: 'wall',
    outlines: [],
    zBase: zMin,
    zTop: zMax,
    materialId: skirtMat,
    groupKey: `wall|${el.levelId}|${el.type.assemblyId ?? 'none'}|${el.id}|skirt`,
    pieces: [],
    placements: [],
    openingInstances: [],
    mesh: acc.build(),
    meshOnly: true,
    skipProjectedOutline: true,
    skirt: true,
    ...(ff.length ? { finishFace: finishFaceOf(ff, skirtNormal(outer, inner)) } : {}),
  }
}

/** the skirt's outward horizontal normal (inner face → outer face, first segment) */
function skirtNormal(outer: Pt2[], inner: Pt2[]): [number, number] {
  const dx = outer[0][0] - inner[0][0], dy = outer[0][1] - inner[0][1]
  const l = Math.hypot(dx, dy) || 1
  return [dx / l, dy / l]
}

/**
 * The wall's NOMINAL vertical extent, before the attach/base derivations: base =
 * level + baseOffsetFt; top = unconnected height, OR the referenced top level
 * (el.paramLevels.topLevelId) + topOffsetFt (TOP-LEVEL round, 2026-08-10 — the
 * columnZRange pattern verbatim: an associated top FLEXES when its level moves;
 * topMode 'level' with no level chosen yet falls back to the height so a
 * half-configured wall never vanishes). Shared with the UI (handle/pick configs)
 * so grips and picking see the same extent the generator builds.
 */
export function wallZRange(el: ResolvedElement): { z0: number; z1: number } | null {
  if (!el.level) return null
  const z0 = el.level.elevationFt + ((el.params.baseOffsetFt as number) || 0)
  const topLevel = el.params.topMode === 'level' ? el.paramLevels?.topLevelId : undefined
  const z1 = topLevel
    ? topLevel.elevationFt + ((el.params.topOffsetFt as number) || 0)
    : z0 + ((el.params.unconnectedHeightFt as number) || 9)
  return z1 - z0 > 1e-6 ? { z0, z1 } : null
}

/**
 * WALL AUTO SKIN round (docs/MODELER.md — Matt: "Gypsum board is set to white but
 * it looks very tan in the model"): the 3D wall BODY has always rendered in ONE
 * material — the assembly's core (structural) layer's, never a finish layer's —
 * so no amount of editing Gypsum Board's color ever reached the model. "per-layer
 * 3D solids" was recorded backlog since 2026-07-21 (P6b) and never picked up.
 *
 * AUTO, no new params (Matt's chosen scope over full per-layer geometric solids):
 * the assembly's EXTERIOR-most layer (`layers[0]` — the wall path's "exterior =
 * left of drawing direction" convention, locationLine.ts) paints the face
 * pointing that way; the INTERIOR-most layer (`layers[length-1]`) paints the
 * opposite face; `flipped` swaps which physical side each lands on, exactly
 * matching the body's own mirror. `Int 2×4` (gyp both ends) naturally buckets
 * both faces into ONE gyp mesh; `Ext … · Stucco/Clapboard/…` naturally splits
 * into a stucco/siding exterior face + a gyp interior face.
 *
 * Classification is a SAFE, ADDITIVE post-process over the wall's OWN
 * already-correct extruded triangles (re-extruding its final, opening-slotted
 * `outlines`, PLUS every header/sill `piece` re-extruded the same way `pieces`
 * already are elsewhere — r2, see below) — never a re-derivation of the
 * offset/miter/void math those already got right. Top/bottom caps are left
 * alone (core, unchanged). A side triangle's outward normal is compared to a
 * LOCAL left-normal; anything not confidently on one side or the other (end
 * caps, corner-miter facets, a piece's own jamb-end caps) is left OFF, so the
 * core body shows through there exactly as before. A classification miss
 * degrades to today's exact appearance; it can never produce a hole or a
 * wrong color. Nudged a hair proud of the core surface (WALL_SKIN_EPS_FT —
 * paint has real thickness) so it never z-fights its own body.
 *
 * r2 (Matt: "you probably skipped portions of walls that have windows and
 * doors placed into them… get it all done"): header/sill pieces are a
 * SEPARATE extrusion axis from the wall body (extrudeProfileAcross sweeps a
 * 2D elevation profile ACROSS the thickness, vs. the body's own vertical
 * sweep of a plan outline) — but the piece's `frame.dir` is the SAME wall-
 * tangent convention the body's centerline uses, so its two big faces get
 * normal exactly ±[-dir.y, dir.x] — the identical left-normal test, just
 * against a CONSTANT direction (one per piece) instead of a sampled one.
 * The piece's own top/bottom and jamb-end bands correctly fall out as
 * ambiguous by the same |nz|/dot thresholds, same as the body's end caps.
 *
 * r3 (Matt: "material settings of the wall extensions up and down to roofs
 * and floors, as well as extensions down to stemwalls"): the flat-prism body
 * above only ever covered the run BELOW/ABOVE the attach/footing cap — the
 * cap mesh itself (`topMesh`/`baseMesh` — deriveAttachedTop/deriveAttachedBase/
 * deriveSteppedBase's own SkinAcc output) stayed core-colored unconditionally.
 * But that mesh's PERIMETER BAND faces are just the wall's own outline edges
 * extruded further up/down (to the roofline, or down to a stem/floor step) —
 * the exact same left-normal-vs-outward-normal test the body classifies by,
 * just walked over a taller/shorter run. Passed in optionally and classified
 * into the SAME buckets with the SAME leftNormalAt (centerline-sampled, so a
 * band face at any point along the wall reads the correct local side) — the
 * sloped/stepped TOP or BOTTOM cap face itself, and the step/jamb faces
 * between differently-pitched pieces, are near-horizontal or run across the
 * thickness and fall out ambiguous by the same thresholds, staying core
 * exactly like a body end-cap.
 *
 * r4 (Matt's screenshots: corner joins still showing the wrong material):
 * corner-weld notch solids (pipeline.ts#weldWallCornerSolids) are a SEPARATE
 * ElementSolid spanning TWO walls at an angle, generated outside wallSolid()
 * entirely — `wallFinishSkins` classifies against ONE wall's convention and
 * can't reach them. `cornerWeldSkins` (below) is the two-wall analogue:
 * classifies the notch's own re-extruded outline PLUS its attach-derived cap
 * mesh (if any) against WHICHEVER of the two walls' left/right split a
 * triangle's normal confidently matches (`classifyMeshInto`'s new `fallback`
 * param — try wall A's convention, then wall B's). The miter tip's own bevel
 * facets run at an angle to BOTH walls and fall out ambiguous by the same
 * thresholds, staying core — same degrade-gracefully contract as everywhere
 * else in this file.
 */
const WALL_SKIN_EPS_FT = 0.01   // ~1/8" proud of the core surface

/** one wall's finish-material split (WALL AUTO SKIN's left/right convention), or
 *  null when there's nothing to classify (a single-layer, core-only assembly).
 *  Shared by the main body/piece classification (`wallFinishSkins`) and the
 *  two-wall corner-notch classification (`cornerWeldSkins`, r4). */
export function wallSkinMaterials(
  el: ResolvedElement,
): { leftMat: string; rightMat: string; coreMat: string | undefined } | null {
  const layers = el.assembly?.layers
  const coreStart = el.assembly?.coreBoundary.start
  if (!layers || layers.length < 2 || coreStart === undefined) return null
  const coreMat = layers[coreStart]?.materialId
  const flipped = el.params.flipped === true
  // physical LEFT face (unflipped exterior side) / physical RIGHT face
  const leftMat = flipped ? layers[layers.length - 1].materialId : layers[0].materialId
  const rightMat = flipped ? layers[0].materialId : layers[layers.length - 1].materialId
  if (leftMat === coreMat && rightMat === coreMat) return null   // nothing would show
  return { leftMat, rightMat, coreMat }
}

/**
 * WALL FINISH SPLITS (2026-10-02, Matt: "place a split line on a wall face to then be
 * able to paint different finishes above and below"): a face's height BANDS in world
 * z, each with the material it shows (a band left unpainted shows the face's own
 * layer). The skin classification below cuts every side triangle of that face at the
 * band lines, so the band edges are exact on the body, the header/sill pieces, the
 * attach/footing caps and the corner notches alike, and each line draws as a real
 * edge (3D and, through the skin's mesh edges, elevations).
 */
export interface SkinBand { z0: number; z1: number; mat: string }
export interface SkinBands { left: SkinBand[] | null; right: SkinBand[] | null }

export function wallSkinBands(el: ResolvedElement): SkinBands | null {
  const ext = faceHasBands(el.params, 'exterior'), int = faceHasBands(el.params, 'interior')
  if (!ext && !int) return null
  const zr = wallZRange(el)
  const layers = el.assembly?.layers
  if (!zr || !layers?.length) return null
  const flipped = el.params.flipped === true
  const extLayer = layers[0].materialId, intLayer = layers[layers.length - 1].materialId
  const bandsOf = (side: 'exterior' | 'interior'): SkinBand[] | null => {
    if (!(side === 'exterior' ? ext : int)) return null
    const own = side === 'exterior' ? extLayer : intLayer
    return faceBands(el.params, side).map((b) => ({ z0: zr.z0 + b.z0, z1: zr.z0 + b.z1, mat: b.materialId || own }))
  }
  const e = bandsOf('exterior'), i = bandsOf('interior')
  return flipped ? { left: i, right: e } : { left: e, right: i }
}

/** the left/right/core materials skins classify with: wallSkinMaterials, or (a
 *  core-only wall that carries bands) the core material on both faces */
function skinConvention(el: ResolvedElement): { leftMat: string; rightMat: string; coreMat: string | undefined; bands: SkinBands | null } | null {
  const mats = wallSkinMaterials(el)
  const bands = wallSkinBands(el)
  if (mats) return { ...mats, bands }
  if (!bands) return null
  const layers = el.assembly?.layers || []
  const core = layers[el.assembly?.coreBoundary.start ?? 0]?.materialId || layers[0]?.materialId || ''
  return { leftMat: core, rightMat: core, coreMat: core, bands }
}

type V3 = [number, number, number]
/** clip a convex polygon to zLo <= z <= zHi (Sutherland-Hodgman, order kept) */
function clipPolyZ(poly: V3[], zLo: number, zHi: number): V3[] {
  let cur = poly
  for (const [z, keepAbove] of [[zLo, true], [zHi, false]] as [number, boolean][]) {
    if (!Number.isFinite(z) || cur.length < 3) continue
    const out: V3[] = []
    const inside = (p: V3) => (keepAbove ? p[2] >= z - 1e-9 : p[2] <= z + 1e-9)
    for (let k = 0; k < cur.length; k++) {
      const a = cur[k], b = cur[(k + 1) % cur.length]
      const ia = inside(a), ib = inside(b)
      if (ia) out.push(a)
      if (ia !== ib) {
        const t = (z - a[2]) / (b[2] - a[2])
        out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, z])
      }
    }
    cur = out
  }
  return cur
}
/** where a triangle crosses the level plane z, or null */
function triCrossZ(tri: V3[], z: number): [V3, V3] | null {
  const pts: V3[] = []
  for (let k = 0; k < 3; k++) {
    const a = tri[k], b = tri[(k + 1) % 3]
    if ((a[2] - z) * (b[2] - z) < 0) {
      const t = (z - a[2]) / (b[2] - a[2])
      pts.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, z])
    }
  }
  return pts.length === 2 ? [pts[0], pts[1]] : null
}

type SkinBucket = { pos: number[]; nrm: number[]; idx: number[]; vc: number; edg?: number[] }

/** classify one mesh's triangles by outward normal vs. a left-normal into the
 *  shared per-material buckets — `leftNormalAt(cx, cy)` may sample per-triangle
 *  (the body, via its centerline) or return a constant (a piece, whose frame.dir
 *  never varies along its short run). `fallback` (r4 — a corner-weld notch
 *  spans TWO walls): when the primary convention is ambiguous, try a SECOND
 *  wall's convention before giving up and leaving the triangle core-colored.
 *  `bands` (WALL FINISH SPLITS): a face that carries bands is cut at the band
 *  lines, each piece into its band's bucket, the lines themselves into edges. */
export function classifyMeshInto(
  buckets: Map<string, SkinBucket>,
  mesh: { positions: Float32Array; normals: Float32Array; indices: Uint32Array },
  leftNormalAt: (cx: number, cy: number) => Pt2,
  leftMat: string, rightMat: string, coreMat: string | undefined,
  fallback?: {
    leftNormalAt: (cx: number, cy: number) => Pt2
    leftMat: string; rightMat: string; coreMat: string | undefined
    bands?: SkinBands | null
  },
  bands?: SkinBands | null,
): void {
  const { positions, normals, indices } = mesh
  const bucketFor = (mat: string) => {
    let b = buckets.get(mat)
    if (!b) { b = { pos: [], nrm: [], idx: [], vc: 0 }; buckets.set(mat, b) }
    return b
  }
  const convs = [
    { leftNormalAt, leftMat, rightMat, coreMat, bands: bands || null },
    ...(fallback ? [{ ...fallback, bands: fallback.bands || null }] : []),
  ]
  const resolve = (nx: number, ny: number, cx: number, cy: number): { mat?: string; bands?: SkinBand[] } | null => {
    for (const c of convs) {
      const [lx, ly] = c.leftNormalAt(cx, cy)
      const dot = nx * lx + ny * ly   // triangle normal · left-normal
      const side = dot > 0.5 ? 'L' : dot < -0.5 ? 'R' : null
      if (!side) continue
      const bs = side === 'L' ? c.bands?.left : c.bands?.right
      if (bs) return { bands: bs }
      const mat = side === 'L' ? c.leftMat : c.rightMat
      if (mat && mat !== c.coreMat) return { mat }
    }
    return null
  }
  const E = WALL_SKIN_EPS_FT
  for (let i = 0; i < indices.length; i += 3) {
    const ia = indices[i], ib = indices[i + 1], ic = indices[i + 2]
    const nx = normals[ia * 3], ny = normals[ia * 3 + 1], nz = normals[ia * 3 + 2]
    if (Math.abs(nz) > 0.5) continue   // top/bottom-facing — core shows through, unchanged
    const cx = (positions[ia * 3] + positions[ib * 3] + positions[ic * 3]) / 3
    const cy = (positions[ia * 3 + 1] + positions[ib * 3 + 1] + positions[ic * 3 + 1]) / 3
    const r = resolve(nx, ny, cx, cy)
    if (!r) continue   // ambiguous under every convention tried, or matches core anyway
    if (r.mat) {
      const b = bucketFor(r.mat)
      for (const idx of [ia, ib, ic]) {
        // nudged along the (shared, flat-shaded) triangle normal — never touches z
        b.pos.push(positions[idx * 3] + nx * E, positions[idx * 3 + 1] + ny * E, positions[idx * 3 + 2])
        b.nrm.push(nx, ny, nz)
        b.idx.push(b.vc++)
      }
      continue
    }
    const tri: V3[] = [ia, ib, ic].map((k) => [positions[k * 3] + nx * E, positions[k * 3 + 1] + ny * E, positions[k * 3 + 2]] as V3)
    const tz0 = Math.min(tri[0][2], tri[1][2], tri[2][2]), tz1 = Math.max(tri[0][2], tri[1][2], tri[2][2])
    for (const band of r.bands!) {
      if (band.z1 <= tz0 + 1e-9 || band.z0 >= tz1 - 1e-9) continue
      const b = bucketFor(band.mat)
      const poly = band.z0 <= tz0 && band.z1 >= tz1 ? tri : clipPolyZ(tri, band.z0, band.z1)
      for (let k = 1; k + 1 < poly.length; k++) {
        for (const q of [poly[0], poly[k], poly[k + 1]]) {
          b.pos.push(q[0], q[1], q[2])
          b.nrm.push(nx, ny, nz)
          b.idx.push(b.vc++)
        }
      }
      // the split line under this band, where it crosses this triangle
      if (Number.isFinite(band.z0)) {
        const seg = triCrossZ(tri, band.z0)
        if (seg) (b.edg || (b.edg = [])).push(...seg[0], ...seg[1])
      }
    }
  }
}

/** buckets → ElementSolid[] (WALL AUTO SKIN's shared output shape) — one solid
 *  per material bucket, z-extent computed from the bucket's OWN accumulated
 *  vertices (not trusted from a caller-supplied scalar, since a bucket can span
 *  taller/shorter than any one nominal run once cap geometry folds in). */
function bucketsToSkinSolids(
  buckets: Map<string, SkinBucket>,
  elementId: string,
  groupPrefix: string,
  // r4: a corner-weld skin is credited to BOTH walls (DocMirror's dirty-tracking
  // keys off contributorIds ?? [elementId] — omitting this on a two-wall solid
  // attributes it to only ONE of the two, so editing the OTHER wall would never
  // mark it dirty). wallFinishSkins' own single-wall solids leave this unset —
  // elementId alone is already the right (and only) contributor there.
  contributorIds?: string[],
): ElementSolid[] {
  if (!buckets.size) return []
  const out: ElementSolid[] = []
  for (const [mat, b] of buckets) {
    if (!b.idx.length) continue
    let bZMin = Infinity, bZMax = -Infinity
    for (let i = 2; i < b.pos.length; i += 3) {
      if (b.pos[i] < bZMin) bZMin = b.pos[i]
      if (b.pos[i] > bZMax) bZMax = b.pos[i]
    }
    out.push({
      elementId,
      ...(contributorIds ? { contributorIds } : {}),
      category: 'wall',
      outlines: [],
      zBase: bZMin, zTop: bZMax,
      materialId: mat,
      groupKey: `${groupPrefix}|${mat}`,
      pieces: [],
      placements: [],
      openingInstances: [],
      mesh: { positions: new Float32Array(b.pos), normals: new Float32Array(b.nrm), indices: new Uint32Array(b.idx), edges: b.edg || [] },
      meshOnly: true,
      skin: true,
      skipProjectedOutline: true,
    })
  }
  return out
}

/**
 * r4 — the corner-weld notch's own finish-material coverage (see the file-level
 * doc comment above). `nA`/`nB` are each wall's CONSTANT left-normal at this
 * corner (`CornerArm.n` in pipeline.ts/cornerWeld.ts) — the notch is a small
 * region right at one end of each wall, where its local tangent doesn't vary,
 * exactly the same "constant per short run" convention header/sill pieces use.
 * `rings`/`z0`/`z1` are the notch's own final outline and z-range (after any
 * attach-derived adjustment); `capMesh` is its own attach-derived cap, if any.
 */
export function cornerWeldSkins(
  elA: ResolvedElement, nA: Pt2,
  elB: ResolvedElement, nB: Pt2,
  elementId: string,
  rings: Pt2[][],
  z0: number, z1: number,
  capMesh?: NonNullable<ElementSolid['mesh']> | null,
): ElementSolid[] {
  const matsA = skinConvention(elA)
  const matsB = skinConvention(elB)
  if (!matsA && !matsB) return []
  const buckets = new Map<string, SkinBucket>()
  const classify = (mesh: { positions: Float32Array; normals: Float32Array; indices: Uint32Array }) => {
    if (matsA && matsB) {
      classifyMeshInto(buckets, mesh, () => nA, matsA.leftMat, matsA.rightMat, matsA.coreMat, {
        leftNormalAt: () => nB, leftMat: matsB.leftMat, rightMat: matsB.rightMat, coreMat: matsB.coreMat, bands: matsB.bands,
      }, matsA.bands)
    } else if (matsA) {
      classifyMeshInto(buckets, mesh, () => nA, matsA.leftMat, matsA.rightMat, matsA.coreMat, undefined, matsA.bands)
    } else if (matsB) {
      classifyMeshInto(buckets, mesh, () => nB, matsB.leftMat, matsB.rightMat, matsB.coreMat, undefined, matsB.bands)
    }
  }
  const body = extrudeOutlines(rings, z0, z1)
  if (body) classify(body)
  if (capMesh) classify(capMesh)
  return bucketsToSkinSolids(
    buckets, elementId, `wall-corner-skin|${z0.toFixed(4)}|${z1.toFixed(4)}|${elA.id}|${elB.id}`,
    [elA.id, elB.id],
  )
}

export function wallFinishSkins(
  el: ResolvedElement,
  centerline: Pt2[],
  outlines: Pt2[][],
  pieces: SolidPiece[],
  zBase: number,
  zTop: number,
  // r3: the attach/footing cap meshes (deriveAttachedTop/deriveAttachedBase/
  // deriveSteppedBase's own SkinAcc output) — classified against the SAME
  // centerline-sampled left-normal as the flat body, since their perimeter
  // bands are just the wall's own outline edges extruded up/down further.
  topMesh?: NonNullable<ElementSolid['mesh']> | null,
  baseMesh?: NonNullable<ElementSolid['mesh']> | null,
): ElementSolid[] {
  if (centerline.length < 2) return []
  const mats = skinConvention(el)
  if (!mats) return []
  const { leftMat, rightMat, coreMat, bands } = mats
  const buckets = new Map<string, SkinBucket>()
  const bodyLeftNormalAt = (cx: number, cy: number): Pt2 => {
    const { s } = projectToPolyline(centerline, [cx, cy])
    const { dir } = polylinePointAt(centerline, s)
    return [-dir[1], dir[0]]
  }
  const body = extrudeOutlines(outlines, zBase, zTop)
  if (body) classifyMeshInto(buckets, body, bodyLeftNormalAt, leftMat, rightMat, coreMat, undefined, bands)
  // r2: header/sill pieces — the SAME classification, against each piece's own
  // constant left-normal (its frame.dir never varies along its short run)
  for (const piece of pieces) {
    const { mesh } = extrudeProfileAcross(piece.profile, piece.frame, piece.thicknessFt)
    if (!mesh) continue
    const [dx, dy] = piece.frame.dir
    classifyMeshInto(buckets, mesh, () => [-dy, dx], leftMat, rightMat, coreMat, undefined, bands)
  }
  // r3: attach/footing caps — same body left-normal (their bands follow the
  // same outline the body's own side faces do, just over a taller/shorter run)
  if (topMesh) classifyMeshInto(buckets, topMesh, bodyLeftNormalAt, leftMat, rightMat, coreMat, undefined, bands)
  if (baseMesh) classifyMeshInto(buckets, baseMesh, bodyLeftNormalAt, leftMat, rightMat, coreMat, undefined, bands)
  // a banded wall's skins carry its own split-line edges: never merged with another wall's
  return bucketsToSkinSolids(buckets, el.id, `wall-skin|${el.levelId}|${el.type.assemblyId ?? 'none'}|${zBase.toFixed(4)}|${zTop.toFixed(4)}${bands ? '|' + el.id : ''}`)
}

export function wallSolid(
  el: ResolvedElement,
  hosted: ResolvedElement[],
  attachTargets: ResolvedElement[] = [],
  byId?: Map<string, ResolvedElement>,
  // BASE ATTACH round: appended last (not inserted before byId) so every existing
  // positional call site keeps working unchanged.
  baseAttachTargets: ResolvedElement[] = [],
  // WALL FRAMING round: same append-last convention — an out-array the derived
  // framing solid (framing.ts) is pushed into automatically for any wood-core wall
  // (default-on, no instance toggle). Built HERE (not in the registry) because the
  // framing follows the derived top/base exactly, and every input it needs — the
  // final prism extents, the clip surfaces, the stepped-base runs, the opening
  // placements — is in scope at the end of this function and nowhere else.
  framingOut?: ElementSolid[],
  // WALL AUTO SKIN round: same append-last convention, same reasoning — the skin
  // needs the FINAL prismBase/zTop (after footing/attach clipping) and the final,
  // opening-slotted `outlines`, both only settled at the end of this function.
  skinOut?: ElementSolid[],
): ElementSolid | null {
  const path = el.params.path as Path2D | null
  if (!path || path.nodes.length < 2) return null
  const t = el.assemblyThicknessFt
  if (t <= 0 || !el.level) return null
  // 2.6a location lines: the stored path is the REFERENCE line; the body derives from
  // the centerline `delta` ft along its left normal (0 = pre-2.6 identity, no offset run)
  const delta = centerlineOffsetFt(el.params.locationLine, el.params.flipped, el.assembly)
  const refLine = flattenPath(path)
  if (refLine.length < 2) return null
  // Closed-loop detection (2.6 polish — Matt's corner-notch report): a chain drawn back
  // to its start is an OPEN path with coincident ends. Treat it as a true ring so the
  // seam corner miters like every other corner — offsetPolyline (face location lines)
  // and the ring inflation below both see a closed path.
  const p0 = path.nodes[0].pt, pL = path.nodes[path.nodes.length - 1].pt
  const isLoop = !path.closed && path.nodes.length >= 4 &&
    Math.hypot(p0[0] - pL[0], p0[1] - pL[1]) < 1e-6
  const workPath: Path2D = isLoop ? { closed: true, nodes: path.nodes.slice(0, -1) } : path
  const centerPath = delta === 0 ? workPath : offsetPolyline(workPath, delta)
  if (!centerPath) return null
  const centerline = flattenPath(centerPath)
  if (centerline.length < 2) return null
  const zr = wallZRange(el)
  if (!zr) return null
  const zBase = zr.z0
  let zTop = zr.z1

  let outlines: Pt2[][]
  if (isLoop) {
    // annulus: outward ring minus inward ring — mitered at every corner incl. the seam
    let ring = centerline
    const r0 = ring[0], rL = ring[ring.length - 1]
    if (ring.length > 1 && Math.hypot(r0[0] - rL[0], r0[1] - rL[1]) < 1e-9) ring = ring.slice(0, -1)
    if (signedArea(ring) < 0) ring = [...ring].reverse()
    const outer = inflateClosedRing(ring, t / 2)
    const inner = inflateClosedRing(ring, -t / 2)
    outlines = differenceClosed(outer, inner)
  } else {
    outlines = inflateOpen(centerline, t / 2)
  }
  if (outlines.length === 0) return null

  // openings keep their hostOffsetFt arc-length semantics along the STORED path (06 §4.2)
  const frame = wallFrame(refLine)
  const placements: OpeningPlacement[] = []
  const seamFramingPlacements: OpeningPlacement[] = []
  const pieces: SolidPiece[] = []
  const openingInstances: OpeningInstance[] = []
  // r7: the pre-slot outline + true void boxes ride along for height-aware consumers
  let fullOutlines = outlines
  const openingVoids: { rect: Pt2[]; zLow: number; zHigh: number; elementId: string }[] = []
  // ATTACH-TARGETS round: the attach derivation runs AFTER the hosted loop (it needs
  // the opening voids to keep them holes in the skin), but header pieces need the
  // FINAL prism top — so the loop defers them and they run once zTop settles.
  // Sills defer the same way to the FINAL prism BASE (WALL-IS-STEM round): a wall
  // extending down to its footing must fill below every opening to the true bottom,
  // never leave the slot open through the below-grade extension.
  // WALL NICHE: rough openings the framing frames (never plan symbols / instances)
  const nichePlacements: OpeningPlacement[] = []
  // POCKET DOORS round: the framing sees a pocket door's FULL rough opening (door +
  // pocket(s)) — one header across, no studs or plate in the pocket
  const framingRO = new Map<string, OpeningPlacement>()
  const headerJobs: ((zTopFinal: number) => void)[] = []
  const sillJobs: ((zBottomFinal: number) => void)[] = []

  for (const opening of hosted) {
    // WALL NICHE (2026-10-07): a recess from ONE face, never through the wall —
    // nicheRecess cuts a partial-depth channel, fills above/below it at that depth,
    // and frames the rough opening like a window (studs move to the niche)
    if (opening.category === 'niche') {
      const r = nicheRecess(opening, frame, t, delta, zBase)
      if (!r) continue
      nichePlacements.push(r.placement)
      openingVoids.push({ rect: r.rect, zLow: r.placement.zLow, zHigh: r.placement.zHead, elementId: opening.id })
      outlines = differenceClosed(outlines, [r.rect])
      const at = frame.pointAt(r.placement.center)
      const nAt: Pt2 = [-at.dir[1], at.dir[0]]
      const o = frame.pointAt(r.placement.s0).pt
      const nicheFrame: ProfileFrame = {
        origin: [o[0] + nAt[0] * (delta + r.across), o[1] + nAt[1] * (delta + r.across)],
        dir: at.dir,
      }
      const pl = r.placement
      const local = (p: Pt2[]): Pt2[] => p.map(([s, z]) => [s - pl.s0, z] as Pt2)
      headerJobs.push((zTopFinal) => {
        const header = headerProfile(pl, zTopFinal)
        if (header) pieces.push({ profile: local(header), frame: nicheFrame, thicknessFt: r.depth, edgeMask: header.map((_, i) => i < header.length - 3) })
      })
      sillJobs.push((zBottomFinal) => {
        const sill = sillProfile(pl, zBottomFinal)
        if (sill) pieces.push({ profile: local(sill), frame: nicheFrame, thicknessFt: r.depth, edgeMask: [false, false, true, false] })
      })
      continue
    }
    // Tier 2 corner sliders: two-host placement pinned at the shared corner — its
    // own path (below); a unit whose corner can't resolve (partner gone, walls
    // separated) falls THROUGH to normal single-host placement on its primary wall
    // and simply stops cutting the partner (graceful degradation, never a crash)
    if (isCornerSliderUnit(opening)) {
      const partnerId = opening.hostId === el.id
        ? (typeof opening.params.hostBId === 'string' ? opening.params.hostBId : '')
        : (opening.hostId || '')
      const partner = partnerId ? byId?.get(partnerId) : undefined
      const partnerPath = partner?.params?.path as Path2D | null | undefined
      const partnerPts = partnerPath ? flattenPath(partnerPath) : []
      const fit = partnerPts.length >= 2
        ? cornerSliderLegFit(opening, el.id, refLine, partnerPts)
        : null
      if (fit) {
        const sFar = fit.sCorner + fit.dirIn * fit.legFt
        const heightFt = (opening.params.heightFt as number) || 8
        // sillFt (2026-08-09): a corner unit can be raised too — same field, same
        // wall-fill-below behavior via sillProfile below
        const sillC = (opening.params.sillFt as number) || 0
        // LEVEL-INDEPENDENT OPENINGS (2026-08-17): same fix as placeOpening's own
        // zBase derivation — a corner unit places from ITS OWN level, not the
        // host wall's, falling back to the wall's zBase only if its level is gone
        const zBaseC = opening.level?.elevationFt ?? zBase
        const placement: OpeningPlacement = {
          elementId: opening.id,
          s0: Math.min(fit.sCorner, sFar),
          s1: Math.max(fit.sCorner, sFar),
          center: (fit.sCorner + sFar) / 2,
          zLow: zBaseC + sillC,
          zHead: zBaseC + sillC + heightFt,
          riseFt: 0,          // corner units are flat-headed by construction
          rakeFt: 0,
          widthFt: fit.legFt,
        }
        placements.push(placement)
        // the cut extends THROUGH the corner past this wall's end by the partner's
        // thickness, so the mitered end region opens fully (the corner header above
        // the unit is the weld prism, z-clipped in pipeline.weldWallCornerSolids)
        const ext = (partner?.assemblyThicknessFt || t) + SLOT_EPS
        const endAt = frame.pointAt(fit.sCorner)
        const inP = frame.pointAt(sFar).pt
        const outward: Pt2 = [-endAt.dir[0] * fit.dirIn, -endAt.dir[1] * fit.dirIn]
        const outP: Pt2 = [endAt.pt[0] + outward[0] * ext, endAt.pt[1] + outward[1] * ext]
        const nC: Pt2 = [-endAt.dir[1], endAt.dir[0]]
        const hh = t / 2 + SLOT_EPS
        const cS: Pt2 = [nC[0] * delta, nC[1] * delta]
        const rect: Pt2[] = [
          [outP[0] + cS[0] + nC[0] * hh, outP[1] + cS[1] + nC[1] * hh],
          [inP[0] + cS[0] + nC[0] * hh, inP[1] + cS[1] + nC[1] * hh],
          [inP[0] + cS[0] - nC[0] * hh, inP[1] + cS[1] - nC[1] * hh],
          [outP[0] + cS[0] - nC[0] * hh, outP[1] + cS[1] - nC[1] * hh],
        ]
        openingVoids.push({ rect, zLow: placement.zLow, zHigh: placement.zHead, elementId: opening.id })
        outlines = differenceClosed(outlines, [rect])

        headerJobs.push((zTopFinal) => {
          const header = headerProfile(placement, zTopFinal)
          if (!header) return
          const o = frame.pointAt(placement.s0).pt
          const shiftH: Pt2 = [nC[0] * delta, nC[1] * delta]
          pieces.push({
            profile: header.map(([s, z]) => [s - placement.s0, z] as Pt2),
            frame: { origin: [o[0] + shiftH[0], o[1] + shiftH[1]], dir: frame.pointAt(placement.center).dir },
            thicknessFt: t,
            edgeMask: header.map((_, i) => i < header.length - 3),
          })
        })
        // sillFt (2026-08-09): the slot removed is FULL WALL HEIGHT (a 2D plan
        // notch, extruded prism base→zTop) — without this piece a raised corner unit
        // would leave the wall open all the way down to the floor. Same shape/mask
        // sillProfile's straight-wall consumer uses (top edge only, real line).
        sillJobs.push((zBottomFinal) => {
          const sillPc = sillProfile(placement, zBottomFinal)
          if (!sillPc) return
          const o = frame.pointAt(placement.s0).pt
          const shiftS: Pt2 = [nC[0] * delta, nC[1] * delta]
          pieces.push({
            profile: sillPc.map(([s, z]) => [s - placement.s0, z] as Pt2),
            frame: { origin: [o[0] + shiftS[0], o[1] + shiftS[1]], dir: frame.pointAt(placement.center).dir },
            thicknessFt: t,
            edgeMask: [false, false, true, false],
          })
        })
        // the 3D instance is emitted ONCE, by the PRIMARY host: pos/rotZ anchor the
        // CORNER with local +x along this wall's inward direction; the chirality
        // variant picks which of the two corner prototypes renders
        if (fit.primary) {
          const inward: Pt2 = [endAt.dir[0] * fit.dirIn, endAt.dir[1] * fit.dirIn]
          const bAtStart = Math.hypot(partnerPts[0][0] - endAt.pt[0], partnerPts[0][1] - endAt.pt[1]) < 0.1
          const bInward = inwardDirAtEnd(partnerPts, bAtStart)
          openingInstances.push({
            opening,
            pos: [endAt.pt[0] + cS[0], endAt.pt[1] + cS[1], placement.zLow],
            rotZ: Math.atan2(inward[1], inward[0]),
            variant: cornerVariantOf(inward, bInward),
          })
        }
        continue
      }
      if (opening.hostId !== el.id) continue   // secondary host, corner unresolved — nothing to cut
      // primary host, corner unresolved — fall through to normal placement below
    }
    const placement = placeOpening(opening, frame, zBase)
    if (!placement) continue // doesn't fit (segment too short / wall too small) — skipped, not crashed
    // DOOR ACROSS A SEAM (openings.ts#openingSeamSplit): a GUEST is the part of a
    // neighbor's door overhanging into this wall — slot + header/sill fill only; the
    // host owns the placement (plan symbol), the instance and the trim
    const isSeamGuest = !!opening.params.__seamGuest
    if (!isSeamGuest) placements.push(placement)
    if (isSeamGuest || placement.s0 < -1e-6 || placement.s1 > frame.totalLength + 1e-6) {
      const lo = Math.max(0, placement.s0), hi = Math.min(frame.totalLength, placement.s1)
      if (hi - lo > 1e-6) seamFramingPlacements.push({ ...placement, s0: lo, s1: hi, center: (lo + hi) / 2, widthFt: hi - lo })
    }

    // POCKET DOORS CARVE THEIR POCKET (2026-10-07 — opening/pocket.ts): a pocketing door
    // cuts a CAVITY beside its opening between two thin skins (the niche channel idea,
    // through the middle of the wall instead of from a face), and a stack too deep for
    // the wall FURS the wall out over door + pocket — no stand-in wall needed
    const pk = isSeamGuest ? null : pocketLayout(opening, el)
    // EXTERIOR POCKETS PARK BEHIND THE WALL: an exterior host is never cut or furred —
    // only the opening goes through; the door sits in the gap behind it (pk.c)
    const cut = pk && !pk.behind ? pk : null
    const bodyLo = cut ? cut.bodyLo : -t / 2, bodyHi = cut ? cut.bodyHi : t / 2
    if (cut?.furred) {
      const ro = pocketRoughOpening(cut, placement.s0, placement.s1)
      const housing = acrossRect(frame, ro.s0 - cut.housingEndFt, ro.s1 + cut.housingEndFt, bodyLo, bodyHi, delta)
      outlines = unionClosed([...outlines, housing])
      fullOutlines = unionClosed([...fullOutlines, housing])
    }
    const rect = cut
      ? acrossRect(frame, placement.s0, placement.s1, bodyLo - SLOT_EPS, bodyHi + SLOT_EPS, delta)
      : slotRect(frame, placement.s0, placement.s1, t / 2, delta)
    openingVoids.push({ rect, zLow: placement.zLow, zHigh: placement.zHead + placement.riseFt, elementId: opening.id })
    outlines = differenceClosed(outlines, [rect])
    if (cut) {
      const ro = pocketRoughOpening(cut, placement.s0, placement.s1)
      framingRO.set(opening.id, { ...placement, s0: ro.s0, s1: ro.s1, center: (ro.s0 + ro.s1) / 2, widthFt: ro.s1 - ro.s0 })
      const cav = cut.cavHi - cut.cavLo, cavMid = (cut.cavLo + cut.cavHi) / 2
      for (const sd of cut.sides) {
        // the channel overlaps the slot a hair so the two voids join cleanly
        const a = sd.dir < 0 ? placement.s0 - sd.lenFt : placement.s1 - 0.01
        const b = sd.dir < 0 ? placement.s0 + 0.01 : placement.s1 + sd.lenFt
        const chan = acrossRect(frame, a, b, cut.cavLo, cut.cavHi, delta)
        outlines = differenceClosed(outlines, [chan])
        openingVoids.push({ rect: chan, zLow: placement.zLow, zHigh: placement.zHead, elementId: opening.id })
        // the pocket is only as tall as the door: wall fills back in above it (and below
        // a raised door), at the cavity's own thickness — the skins carry on either side
        const pl: OpeningPlacement = { ...placement, s0: a, s1: b, center: (a + b) / 2, widthFt: b - a, riseFt: 0, rakeFt: 0 }
        const at0 = frame.pointAtExt(pl.center)
        const nA: Pt2 = [-at0.dir[1], at0.dir[0]]
        const o0 = frame.pointAtExt(a).pt
        const chanFrame: ProfileFrame = { origin: [o0[0] + nA[0] * (delta + cavMid), o0[1] + nA[1] * (delta + cavMid)], dir: at0.dir }
        const localC = (q: Pt2[]): Pt2[] => q.map(([ss, z]) => [ss - a, z] as Pt2)
        headerJobs.push((zTopFinal) => {
          const h = headerProfile(pl, zTopFinal)
          if (h) pieces.push({ profile: localC(h), frame: chanFrame, thicknessFt: cav, edgeMask: h.map((_, i) => i < h.length - 3) })
        })
        sillJobs.push((zBottomFinal) => {
          const sl = sillProfile(pl, zBottomFinal)
          if (sl) pieces.push({ profile: localC(sl), frame: chanFrame, thicknessFt: cav, edgeMask: [false, false, true, false] })
        })
      }
    }

    // pointAtExt: identical to pointAt on the wall; past a seam it continues straight
    // (a guest's center, an overhanging opening's far jamb)
    const at = frame.pointAtExt(placement.center)
    const nAt: Pt2 = [-at.dir[1], at.dir[0]]
    const shift: Pt2 = [nAt[0] * delta, nAt[1] * delta]   // into the offset body
    const o = frame.pointAtExt(placement.s0).pt
    // POCKET DOORS round: a furred pocket wall's header/sill fill spans the housing too
    const bodyMid = (bodyLo + bodyHi) / 2, bodyT = bodyHi - bodyLo
    const pieceFrame: ProfileFrame = {
      origin: [o[0] + shift[0] + nAt[0] * bodyMid, o[1] + shift[1] + nAt[1] * bodyMid],
      dir: at.dir,
    }
    // piece profiles are LOCAL to the piece frame (s measured from placement.s0)
    const local = (p: Pt2[]): Pt2[] => p.map(([s, z]) => [s - placement.s0, z] as Pt2)
    // feature-edge masks: a header's underside (head line + arch — the first
    // vertexCount-3 edges, see headerProfile's ring order) and a sill's top line
    // (edge 2) are the only real lines; sides/outer edges are flush with the face
    // STACKED OPENINGS (2026-09-29, Santiago: an upper-story window above a lower
    // one in the same tall wall): the fill above/below an opening must not bury
    // ANOTHER opening that shares its stretch of wall — its sill piece used to run
    // wall-base → its own sill straight through the window below. Only walls with
    // overlapping openings take this path; everything else is byte-identical.
    // DOOR ACROSS A SEAM: an opening overhanging this wall's end keeps only its own
    // share of header/sill fill — the partner wall builds the rest
    const wallL = frame.totalLength
    const seamLo = Math.max(0, placement.s0), seamHi = Math.min(wallL, placement.s1)
    const overhangs = placement.s0 < -1e-6 || placement.s1 > wallL + 1e-6
    const carve = (profile: Pt2[], zTop: number, zBot: number, mask: boolean[]) => {
      const others = placements.filter((q) => q !== placement
        && q.s1 > placement.s0 + 1e-6 && q.s0 < placement.s1 - 1e-6)
      if ((!others.length && !overhangs) || !clipperReady()) return [{ profile, mask }]
      let rings = others.length ? differenceClosed([profile], others.map(openingProfile)) : [profile]
      if (overhangs) {
        if (seamHi - seamLo < 1e-6) return []
        rings = intersectClosed(rings, [[[seamLo, -1e5], [seamHi, -1e5], [seamHi, 1e5], [seamLo, 1e5]]])
      }
      rings = rings.filter((r) => r.length >= 3)
      const flat = (a: number, b: number) => Math.abs(a - b) < 1e-6
      return rings.map((r) => ({
        profile: r,
        // a real line everywhere except the slot sides and the wall top/base
        mask: r.map((a, i) => {
          const b = r[(i + 1) % r.length]
          if (flat(a[0], b[0]) && (flat(a[0], placement.s0) || flat(a[0], placement.s1))) return false
          if (overhangs && flat(a[0], b[0]) && (flat(a[0], seamLo) || flat(a[0], seamHi))) return false
          if (flat(a[1], b[1]) && (flat(a[1], zTop) || flat(a[1], zBot))) return false
          return true
        }),
      }))
    }
    headerJobs.push((zTopFinal) => {
      const header = headerProfile(placement, zTopFinal)
      if (!header) return
      for (const pc of carve(header, zTopFinal, Number.NaN, header.map((_, i) => i < header.length - 3))) {
        pieces.push({ profile: local(pc.profile), frame: pieceFrame, thicknessFt: bodyT, edgeMask: pc.mask })
      }
    })
    sillJobs.push((zBottomFinal) => {
      const sill = sillProfile(placement, zBottomFinal)
      if (!sill) return
      for (const pc of carve(sill, Number.NaN, zBottomFinal, [false, false, true, false])) {
        pieces.push({ profile: local(pc.profile), frame: pieceFrame, thicknessFt: bodyT, edgeMask: pc.mask })
      }
    })

    if (isSeamGuest) continue   // the host wall emits the door/window itself

    // EXTERIOR TRIM round (2026-10-02, Matt: 3D follows each opening's frame mount — it
    // was always centered here while plan honored it): the prototype's frame is centered
    // on its local y=0, so the instance shifts across the wall to the frame's own center
    // (kernel openingFrameFaces — the same rule the plan draws from)
    const isGate = opening.category === 'door' && isGateOp(String(opening.typeParams.operation))
    const mountFaces = openingFrameFaces(
      resolveFrameMount({ ...opening.typeParams, ...opening.params }, el.typeParams?.function),
      el.params.flipped, el.assembly, resolveOpeningFrame(opening.category, { ...opening.typeParams, ...opening.params }).depthFt,
      opening.params.frameRecessFt,
    )
    // SITE GATES: frameless — nothing to mount, the gate places itself off the wall
    // centerline (gate.ts)
    // POCKET DOORS round: a pocket door sits on its pocket's track center (pocket.ts)
    const mountOff = isGate ? 0 : pk ? pk.c : (mountFaces.lo + mountFaces.hi) / 2
    // the host facts the prototype needs ride on the instance's own copy (never the
    // model) — BI-PARTING SLIDERS FACE THE ROOM (interior side), SITE GATES (host
    // thickness), PROJECT STYLE phase 2 (which local side faces out), POCKET DOORS
    // (pocket body depth). One helper, shared with walk mode (opening/hostStamp.ts)
    const instOpening = stampOpeningForHost(opening, el)
    openingInstances.push({
      opening: instOpening,
      pos: [at.pt[0] + shift[0] + nAt[0] * mountOff, at.pt[1] + shift[1] + nAt[1] * mountOff, placement.zLow],
      rotZ: Math.atan2(at.dir[1], at.dir[0]),
    })
  }

  // ATTACH-TARGETS round: derive the attached top from the UNSLOTTED body (the top
  // region spans it) with the opening voids kept as holes in the skin. Clipper-gated —
  // before WASM init (main-thread verticalPick) this contributes nothing, the same
  // graceful fallback as the counter join. Targets come from registry.ts: the picked
  // topAttachIds elements, or every roof for a legacy topAttach:'roof' wall.
  let topMesh: NonNullable<ElementSolid['mesh']> | null = null
  // TEAR r7: a wall carrying BOTH attaches (the knee wall between a torn
  // roof's stacked sheets) passes both derivations the SAME two surface lists
  // so their claims resolve to one paired stretch (kneePairAt). One-sided
  // walls pass nothing — byte-identical to before.
  const knee: KneeSurfaces | undefined =
    attachTargets.length > 0 && baseAttachTargets.length > 0 && !el.params.footingEnabled
      ? {
          floors: baseAttachTargets.flatMap((tg) => targetTopSurfaces(tg)),
          ceils: attachTargets.flatMap((tg) => targetSurfaces(tg)),
        }
      : undefined
  // FRAMING SESSION Phase 3 — FLOOR BEARING ON A wallIsStem WALL (doctrine:
  // BEARING INTENT IS DECLARED BY MODELED OVERLAP — no toggles; "applies to
  // wallIsStem walls identically"): a wood-framed floor overlapping this
  // concrete wall in plan implies bearing — the WALL TOP (it IS the stem)
  // derives DOWN to just under the floor framing, with a single MUDSILL riding
  // on it. Qualification samples the wall's own centerline: a slab edge
  // modeled to the wall's inner face never covers it, which IS the geometric
  // opt-out. The drop itself is deriveAttachedTop's ordinary region clipping —
  // the floor's rings at the mudsill seat elevation, appended after any real
  // attach targets.
  const bearingBands: FloorPlatformBand[] = []
  if (el.params.wallIsStem === true && el.params.footingEnabled && byId && clipperReady()) {
    const probe: Pt2[] = []
    for (let i = 0; i + 1 < centerline.length; i++) {
      const A = centerline[i], B = centerline[i + 1]
      const len = Math.hypot(B[0] - A[0], B[1] - A[1])
      const n = Math.max(1, Math.ceil(len))
      for (let k = 0; k <= n; k++) probe.push([A[0] + ((B[0] - A[0]) * k) / n, A[1] + ((B[1] - A[1]) * k) / n])
    }
    byId.forEach((cand) => {
      const band = floorPlatformBand(cand)
      if (!band) return
      const seat = band.zJoistBot - PLATE_THICKNESS_IN / 12
      if (seat <= zBase + 0.1) return   // below the wall — not a bearing read
      // DERIVE DOWN ONLY (Matt's 2026-08-13 field report — a 6″ wallIsStem curb
      // was being EXTENDED 7ft up to the storey floor above): a floor whose
      // mudsill seat sits AT/ABOVE the wall's own top is a floor the wall never
      // reaches — a modeled gap, not a bearing (footing.ts's stem-run rule,
      // `run.stemTop > seat`, applied here too). Without this guard the band
      // rode deriveAttachedTop's ordinary attach semantics, which EXTEND a wall
      // up to its clip surface — the exact opposite of the stem-bearing intent.
      if (seat >= zTop - 1e-6) return
      if (probe.some((p) => band.rings.some((r) => pointInRingW(p, r)))) bearingBands.push(band)
    })
  }
  if ((attachTargets.length || bearingBands.length) && clipperReady()) {
    const att = deriveAttachedTop(
      fullOutlines, zBase, zTop, attachTargets, openingVoids, knee,
      bearingBands.map((b) => [{ rings: b.rings, zAt: () => b.zJoistBot - PLATE_THICKNESS_IN / 12 }]),
    )
    if (att) {
      zTop = att.prismTop
      topMesh = att.mesh
    }
  }
  // the wallIsStem MUDSILL — a single bearing plate on the derived wall top,
  // over exactly the overlapped region; rides the wall's framing chunk
  if (framingOut && bearingBands.length && clipperReady()) {
    for (const band of bearingBands) {
      const rings = intersectClosed(fullOutlines, band.rings).filter((r) => r.length >= 3)
      if (!rings.length) continue
      framingOut.push({
        elementId: el.id,
        category: 'wall',
        outlines: rings,
        zBase: band.zJoistBot - PLATE_THICKNESS_IN / 12,
        zTop: band.zJoistBot,
        materialId: isWoodCoreWall(el) ? framingMaterialId(el) : 'default',
        groupKey: `wall|${el.levelId}|${el.type.assemblyId ?? 'none'}|${el.id}|framing`,
        pieces: [],
        placements: [],
        openingInstances: [],
        skipProjectedOutline: true,
        framing: true,
      })
    }
  }
  // TRIM round §8.12 — arced wall TOP (DIRECTION FIX 2026-08-20: the arc reads in
  // the wall FACE, along the run's length — see wallArcCapMesh). The band rides the
  // topMesh channel — suppressTopRing, the mesh merge, and the skin/framing
  // stop-at-prism-top behavior all come free, and the cap↔prism seam draws no line
  // (the "merged, reads as one wall" requirement). An ATTACHED top wins (a raked
  // clip can't cap); an opening whose head reaches into the cap band falls back to
  // flat rather than cutting the cap (v1 guard); a closed-loop wall has no "ends"
  // for an arc to span, so it stays flat too.
  if (!topMesh && !isLoop && !workPath.closed) {
    const capKind = el.params.topProfile
    const capRise = Math.max(((el.params.topProfileRiseIn as number) || 0) / 12, 0)
    if ((capKind === 'archConvex' || capKind === 'archConcave') && capRise > 0) {
      const cap = wallArcCapMesh(centerline, t, capKind as 'archConvex' | 'archConcave', capRise, 'top', zTop)
      if (cap) {
        const zTopEff = zTop - cap.bandFt
        if (zTopEff > zBase + 0.25 && !openingVoids.some((v) => v.zHigh > zTopEff - 1e-6)) {
          zTop = zTopEff
          topMesh = cap.mesh
        }
      }
    }
  }
  // header pieces fill from each opening head to the FINAL prism top
  for (const job of headerJobs) job(zTop)

  // CURB round: a topo-mode footed wall lands on its stem steps — the body's bottom
  // derives from the foundation layout wherever the stem rose past the wall base.
  // WALL-IS-STEM round (2026-08-10): a wall that IS its own stem instead extends
  // DOWN to the footing tops (per stepped run) — same machinery, opposite direction.
  let baseMesh: NonNullable<ElementSolid['mesh']> | null = null
  let prismBase = zBase
  // PLAN-CUT VISIBILITY fix (2026-08-17, Matt's report — a footed wall on a
  // stepped/terrain site vanished from plan cuts below its highest step):
  // `prismBase` is the UNIFORM prism's own base (deliberately the highest
  // point across every stepped/attached piece — see deriveSteppedBase/
  // deriveAttachedBase); it's correct for building the prism/sill/framing,
  // but WRONG as "how far down this element's real geometry reaches" — the
  // skinned mesh below it is real, rendered geometry a 2D cut must still
  // find. `zBaseReported` tracks the TRUE lowest point instead; it's what
  // the final solid below actually reports as its own zBase.
  let zBaseReported = zBase
  // WALL FRAMING round: the stepped bottoms ride along so the bottom plate can follow
  // the stem/curb steps the body's own base derives from
  let framingBaseRuns: { s0: number; s1: number; z: number }[] = []
  if (el.params.footingEnabled && clipperReady()) {
    const layout = foundationLayoutFor(el, byId)
    if (layout) {
      const isStem = el.params.wallIsStem === true
      // FRAMING SESSION Phase 3: over a floor-bearing run the wall body AND its
      // framing start on the floor's SHEATHING (r.floorTop — the platform sole
      // plate seat), not on the lowered stem; the floor's own edge fills between
      const bottoms = layout.runs.map((r) => ({ s0: r.s0, s1: r.s1, z: isStem ? r.footingTop : (r.floorTop ?? r.stemTop) }))
      framingBaseRuns = bottoms
      if (bottoms.some((b) => Math.abs(b.z - zBase) > 1e-6)) {
        const trim = deriveSteppedBase(fullOutlines, zBase, zTop, layout, bottoms, t, openingVoids)
        if (trim) {
          prismBase = trim.prismBase
          baseMesh = trim.mesh
          zBaseReported = trim.trueMinZ
        }
      }
    }
  } else if (baseAttachTargets.length && clipperReady()) {
    // BASE ATTACH round: the mirror of the top-attach derivation above — clips the
    // wall's base UP against its picked targets' TOP surfaces. Mutually exclusive
    // with footingEnabled (guarded above and at the pet-palette/UI level too — a
    // wall standing on a roof has no footing).
    const atb = deriveAttachedBase(fullOutlines, zBase, zTop, baseAttachTargets, knee, openingVoids)
    if (atb) {
      prismBase = atb.prismBase
      baseMesh = atb.mesh
      zBaseReported = atb.trueMinZ
    }
  }
  // TRIM round §8.12 — arced wall BOTTOM (DIRECTION FIX 2026-08-20): the mirror of
  // the top band on the baseMesh channel — archConcave here IS the arched underside
  // spanning the run (the facade calibration's scallop/arch read: ends at the true
  // base, rising to the arc apex at mid-span). Footed/stem/base-attached walls win;
  // an opening reaching into the cap band falls back to flat; closed loops stay flat.
  if (!baseMesh && !el.params.footingEnabled && !isLoop && !workPath.closed) {
    const capKind = el.params.bottomProfile
    const capRise = Math.max(((el.params.bottomProfileRiseIn as number) || 0) / 12, 0)
    if ((capKind === 'archConvex' || capKind === 'archConcave') && capRise > 0) {
      const cap = wallArcCapMesh(centerline, t, capKind as 'archConvex' | 'archConcave', capRise, 'bottom', zBase)
      if (cap) {
        const baseEff = zBase + cap.bandFt
        if (baseEff < zTop - 0.25 && !openingVoids.some((v) => v.zLow < baseEff + 1e-6)) {
          prismBase = baseEff
          baseMesh = cap.mesh
          // zBaseReported stays zBase — the band is real geometry reaching the true
          // bottom, so plan-cut inclusion (zExtentBase) keeps seeing the full band
        }
      }
    }
  }
  // sill pieces fill from each opening down to the FINAL prism base — below-grade
  // extensions stay solid under openings, raised (curbed) bases never overfill
  for (const job of sillJobs) job(prismBase)

  const mesh = topMesh && baseMesh ? mergeMeshes(topMesh, baseMesh) : (topMesh ?? baseMesh)

  // WALL FRAMING round: default-on for any wood-core wall — the check here is a
  // cheap early-out (a non-wood wall pays literally nothing; framing.ts checks the
  // same thing internally, this just skips building the ctx object for a wall that
  // can never frame). The clip surfaces mirror exactly what the body derivations
  // above clipped against (same clipperReady gates), so the framing can never
  // disagree with the derived top/base it fits under.
  if (framingOut && isWoodCoreWall(el)) {
    // FRAMING SESSION Phase 3 — the wall's MID-SPAN platform bands: wood-core
    // framed floors whose band genuinely crosses this wall's clear framing
    // height (a floor at the very top or bottom is an attach/bearing story,
    // not a platform crossing). Ring coverage resolves per run inside the
    // generator; this just gathers + height-qualifies the candidates.
    const floorBands: FloorPlatformBand[] = []
    // FLOOR-TO-FLOOR TOP PLATE round (2026-08-17, Matt: a wall modeled floor-to-
    // floor — topMode:'level' set to the very next level, no top-attach pick —
    // never got a floor genuinely CROSSING it; the floor sitting at its own top
    // was excluded below as "an attach/bearing story," but no attach/bearing
    // story was actually active. Those excluded-at-the-top candidates become
    // TERMINAL bands instead of being dropped — the double top plate caps under
    // their joists rather than running flush into them. Only when there's no
    // explicit top-attach (that's ITS job, via plateSurfs) and the wall isn't
    // wallIsStem (a bearing-story top, already its own mechanism).
    const topFloorBands: FloorPlatformBand[] = []
    const hasTopAttach = attachTargets.length > 0
    if (byId && clipperReady()) {
      const MIN_SEG_FT = 0.5
      // FRAMED CRAWL SPACE follow-up (Matt's joists-through-studs screenshot): the
      // bottom qualification used to read the PRISM base — the HIGHEST stepped
      // bottom — so a crawl wall whose uphill stem top sits just under the joists
      // rejected its own floor band WALL-WIDE and the crawl studs ran straight
      // through the floor framing. Qualify against the LOWEST derived bottom
      // instead; stretches with no clear framing height below the band degrade
      // per-station inside the generator (degenerate segments emit nothing, and
      // the platform plates skip stretches whose base sits at/above the band).
      const minBottom = framingBaseRuns.length
        ? Math.min(prismBase, ...framingBaseRuns.map((b) => b.z))
        : prismBase
      byId.forEach((cand) => {
        const band = floorPlatformBand(cand)
        if (!band) return
        if (band.zJoistBot < minBottom + PLATE_THICKNESS_IN / 12 + MIN_SEG_FT) return
        if (band.zSheathTop > zTop - 2 * (PLATE_THICKNESS_IN / 12) - MIN_SEG_FT) {
          if (!hasTopAttach) topFloorBands.push(band)
          return
        }
        floorBands.push(band)
      })
    }
    const framing = buildWallFraming({
      el, workPath, delta, t,
      zTopNominal: zr.z1, prismTop: zTop,
      zBaseNominal: zBase, prismBase,
      // DOOR ACROSS A SEAM: framing sees each opening only over THIS wall's own
      // length — its own overhang trimmed, a neighbor's overhang (guest) included
      placements: [
        ...(seamFramingPlacements.length
          ? [...placements.filter((p) => !seamFramingPlacements.some((q) => q.elementId === p.elementId)), ...seamFramingPlacements]
          : placements).map((p) => framingRO.get(p.elementId) ?? p),
        ...nichePlacements,
      ],
      plateSurfs: attachTargets.length && clipperReady()
        ? attachTargets.flatMap((tg) => plateSurfsFor(tg)) : [],
      baseSurfs: !el.params.footingEnabled && baseAttachTargets.length && clipperReady()
        ? baseAttachTargets.flatMap((tg) => targetTopSurfaces(tg)) : [],
      baseRuns: framingBaseRuns,
      floorBands,
      topFloorBands,
    })
    if (framing) framingOut.push(framing)
  }

  if (outlines.length === 0 && pieces.length === 0) return null
  // WALL AUTO SKIN round (r2 — Matt's screenshot: "some walls still look like the
  // framing color"): the ORIGINAL v1 skipped this whole block whenever topMesh/
  // baseMesh existed (an attach-to-roof or a footing/stem), thinking a flat-prism
  // skin couldn't follow a sloped/stepped cut — but that's wrong: `prismBase`/
  // `zTop` right here ARE ALREADY the attach-adjusted flat-prism extent (the exact
  // same values the primary body's own side faces extrude between — topMesh/
  // baseMesh only cap ABOVE/BELOW this range, never overlapping it). So the skin
  // covers precisely the same flat run the core body's side faces cover, no
  // matter what caps it — which in practice is MOST exterior walls (attached to a
  // roof) and every footed/stem wall, not an edge case. Only the small sloped/
  // stepped cap sliver itself stays core-colored (still ambiguous-normal, still
  // an accepted v1 gap — ~1' worth of wall right at the roof line or the footing
  // step, not the whole wall).
  if (skinOut) {
    skinOut.push(...wallFinishSkins(el, centerline, outlines, pieces, prismBase, zTop, topMesh, baseMesh))
    // FINISH SKIRT round: the exterior finish + sheathing extended down past the
    // derived base (across the floor band / down over the concrete — see
    // buildFinishSkirt). Rides the same out-channel as the skins.
    const skirt = buildFinishSkirt(el, workPath, refLine, frame, delta, t, zBase, isLoop, byId,
      placements.map((pl) => ({ s0: pl.s0, s1: pl.s1, zLow: pl.zLow })))
    if (skirt) skinOut.push(skirt)
  }
  const core = el.assembly?.layers[el.assembly.coreBoundary.start]
  // CURVES round: the wall's own face circles (empty for a bulge-free wall) — the
  // SAME circles the true-arc 2D linework channel re-fits `outlines` against, since
  // `outlines` here is that exact ±t/2-from-centerline boundary, just extruded
  // instead of drawn. Clipper's InflatePaths64 miter-offsets the flattened centerline
  // rather than running our own exact concentric-offset math, but for a tessellation
  // this fine the two agree well within the classifier's fit tolerance (verified by
  // the curves2d.test.ts "true-arc linework" tests against the same Clipper output).
  const circles = wallFaceCircles(el)
  // FRAMING SESSION fix round (Matt, 2026-08-13 — "add any concrete walls to
  // the foundation layer, only the concrete core"): a concrete-cored wall's
  // BODY (this prism — the core, never the separately-emitted finish skins)
  // classifies under the viewport's FOUNDATIONS system alongside stems and
  // footings. `isConcreteCoreWall` covers both a poured/CMU wall modeled as a
  // full wall element and a `wallIsStem` wall (always concrete by the
  // schema's own rule). The `|foundation` token is what sceneStore's
  // systemOfChunk reads — the same convention slabSolid uses for concrete
  // floor slabs.
  const foundationTag = isConcreteCoreWall(el) ? '|foundation' : ''
  return {
    elementId: el.id,
    category: 'wall',
    outlines,
    // PLAN-CUT VISIBILITY fix (2026-08-17): `zBase` here MUST stay the uniform
    // prism's own base (`prismBase`) — `extrudeOutlines`/`prismEdges` extrude the
    // real 3D solid from `zBase` to `zTop`, and a stepped/attached base's skin
    // mesh below `prismBase` is a SEPARATE, already-complete piece of geometry
    // (its own bottom cap + perimeter walls); reporting the skin's lower reach
    // here instead would make the prism extrude too far down and DOUBLE UP with
    // the skin. `zExtentBase` below carries the true lower reach for cut-
    // inclusion callers only.
    zBase: prismBase,
    zTop,
    materialId: core?.materialId ?? 'default',
    groupKey: `wall|${el.levelId}|${el.type.assemblyId ?? 'none'}|${prismBase.toFixed(4)}|${zTop.toFixed(4)}${foundationTag}`,
    pieces,
    placements,
    openingInstances,
    ...(placements.length || openingVoids.length ? { fullOutlines, openingVoids } : {}),
    ...(mesh ? { mesh } : {}),
    ...(topMesh ? { suppressTopRing: true } : {}),
    ...(baseMesh ? { suppressBottomRing: true } : {}),
    ...(circles.length ? { circles } : {}),
    // PLAN-CUT VISIBILITY fix (2026-08-17, Matt's Forsyth report): a footed wall
    // on a stepped/terrain site vanished from a plan cut below its highest step
    // — `zBase` above (the uniform prism's own base) is correct for building
    // geometry but wrong for "how far down this wall's real geometry reaches."
    // `zExtentBase` is that true lower reach (the stepped/attached skin's own
    // bottom) — read ONLY by 2D cut-inclusion tests (plan.ts), never by anything
    // that builds actual mesh/outline geometry. Omitted when it doesn't differ
    // from `zBase` (the ordinary, unstepped case).
    ...(zBaseReported < prismBase - 1e-6 ? { zExtentBase: zBaseReported } : {}),
  }
}
